import { Controller } from "stimulus";
import consumer from "../channels/consumer";

const LIVE_STATUS = "live";
const PENDING_STATUS = "pending";
const IDLE_STATUS = "idle";
const ERROR_STATUS = "error";
const FINALIZATION_TIMEOUT_MS = 4000;
const FIRST_CHUNK_TIMEOUT_MS = 5000;
const PCM_BUFFER_SIZE = 4096;
const PCM_SUPPORTED_SAMPLE_RATES = [8000, 16000, 44100, 48000];
// DeepL recommends PCM at 16 kHz; capturing natively at this rate avoids client-side
// resampling and matches the rate speech models are tuned for.
const PREFERRED_DEEPL_SAMPLE_RATE = 16000;
// Samples per Float32 frame the AudioWorklet posts back to the main thread. Keeps the
// postMessage rate modest (~16/s at 16 kHz) while staying well below one 100 ms chunk.
const WORKLET_FRAME_SIZE = 1024;
// Slightly permissive on purpose: quiet trailing words must keep registering as voice, or the
// silence clock starts mid-sentence and the hallucination gate can eat the real phrase ending.
// The gate itself now guards against the extra noise this lets through.
const VOICE_ACTIVITY_THRESHOLD = 0.007;
// DeepL closes idle realtime sessions ("Timeout. (Reason Code: 9040800)"), so during
// silence-suspension a short silent chunk is injected every ~2 s to keep the session alive.
const DEEPL_CHUNK_KEEPALIVE_INTERVAL_MS = 2000;
const DEEPL_CHUNK_KEEPALIVE_MAX_SILENCE_MS = 2000;
// Transparent reconnect budget after an unexpected DeepL close; refilled as soon as the
// reconnected socket delivers a message, so long meetings survive repeated idle timeouts
// without asking the user to relaunch.
const MAX_RECONNECT_ATTEMPTS = 3;
const RECONNECT_BACKOFF_MS = 750;
// Only attach a concluded translation to a source line spoken recently. Without this window, one
// line that never receives its translation permanently shifts every later translation onto
// ever-older lines, producing interleaved mixed-language fragments.
const TRANSLATION_ATTACH_WINDOW_MS = 15000;
// Minimum suffix/prefix overlap accepted as evidence that DeepL re-sent part of a segment.
// One or two shared characters are coincidence — "conversation" + "normale" share an "n", and
// blindly deduplicating that glued the words into "conversationormale".
const MIN_TRANSCRIPT_OVERLAP_CHARS = 3;
// Characters that mark a word boundary when validating an overlap or containment match.
const WORD_BOUNDARY_CHARS = /[\s.,;:!?…]/;
// DeepL re-sends/continues concluded segments; with two speakers those can arrive after the other
// speaker's line was interleaved, so the line they belong to is no longer the buffer tail. Merge
// into a non-tail line only within this window so a phrase genuinely repeated later still gets
// its own line.
const INTERLEAVED_MERGE_WINDOW_MS = 8000;
// Require this many voiced chunks (~100 ms each) before (re)opening transmission, so transient
// echo/mic-noise blips don't reach DeepL and get transcribed as phantom fragments. Brief
// sub-threshold dips inside a word do NOT reset the count (see SUSPEND_RESET_SILENCE_CHUNKS), so
// quiet/choppy speech still resumes. The pre-roll replays these leading chunks so onsets aren't clipped.
const AUTO_SPEECH_START_CHUNKS = 3;
// While suspended, only zero the voiced-chunk count after this many *consecutive* sub-threshold
// chunks. Tolerates single-chunk dips mid-word (which would otherwise strand quiet speech in the
// suspended state) while still discarding short noise blips.
const SUSPEND_RESET_SILENCE_CHUNKS = 2;
// Keep sending this long after the last detected voice so trailing words aren't clipped, then
// suspend transmission (the DeepL session stays open; only keepalive silence is sent). Every
// suspend/resume cycle makes DeepL split, re-send and sometimes mishear segments, so ordinary
// mid-sentence thinking pauses must NOT trigger it — only real inter-phrase silence.
const SILENCE_SUSPEND_MS = 2000;
// Transcript updates arriving more than this long after the last detected voice cannot
// correspond to real speech (DeepL results lag audio by a couple of seconds at most) and are
// treated as silence hallucinations. 4 s covers DeepL's conclusion lag on trailing words while
// closing the window on phantom phrases faster than the previous 6 s.
const HALLUCINATION_SILENCE_GATE_MS = 4000;
// Recent sub-threshold chunks retained while suspended and replayed on resume so word onsets
// that ramp up from silence aren't clipped (~800 ms at the 100 ms chunk size).
const PREROLL_CHUNK_LIMIT = 8;
const BASE_LANGUAGE = "fr";
const CAPTION_LINE_GROUP_WINDOW_MS = 2500;
const CAPTION_LINE_MAX_LENGTH = 140;
const MICROPHONE_STORAGE_KEY = "live-captions-selected-microphone-id";
const CAPTION_FONT_SIZE_STORAGE_KEY = "live-captions-font-size-index";
const CAPTION_COLOR_STORAGE_KEY = "live-captions-color-index";
// Cycled by the "tT" button; applied to the transcript via the --lc-caption-font-size variable.
const CAPTION_FONT_SIZES = [
  { label: "Petite", value: "15px" },
  { label: "Standard", value: "18px" },
  { label: "Grande", value: "22px" },
  { label: "Tres grande", value: "27px" }
];
// Cycled by the color button; applied via the --lc-caption-color variable.
const CAPTION_COLORS = [
  { label: "Blanc", value: "#ffffff" },
  { label: "Jaune", value: "#ffd54a" },
  { label: "Cyan", value: "#4fd2ff" },
  { label: "Vert", value: "#7cfc62" }
];
// Avatar background palette; a stable color is picked per speaker from a hash of their key.
const SPEAKER_AVATAR_COLORS = [
  "#4687ed",
  "#7c4dff",
  "#00897b",
  "#e91e63",
  "#f57c00",
  "#3949ab",
  "#00acc1",
  "#43a047"
];
const WEGLOT_TO_DEEPL_TARGET_LANGUAGE = {
  ar: "ar",
  de: "de",
  en: "en-US",
  "en-us": "en-US",
  "en-gb": "en-GB",
  es: "es",
  fr: "fr",
  it: "it",
  ja: "ja",
  ko: "ko",
  nl: "nl",
  pl: "pl",
  pt: "pt-PT",
  "pt-br": "pt-BR",
  "pt-pt": "pt-PT",
  tr: "tr",
  uk: "uk",
  zh: "zh-HANS",
  "zh-cn": "zh-HANS",
  "zh-hans": "zh-HANS"
};

// AudioWorklet processor source. Runs on the audio render thread, buffers mono Float32
// samples into fixed-size frames, and posts them (transferring the backing buffer) to the
// controller on the main thread. Shipped as a Blob URL so it works with the legacy Webpack
// build without a separate entry point (CSP already allows blob: in script-src/worker-src).
const CAPTURE_WORKLET_SOURCE = `
class PcmCaptureProcessor extends AudioWorkletProcessor {
  constructor(options) {
    super();
    const opts = (options && options.processorOptions) || {};
    this.frameSize = opts.frameSize || 1024;
    this._buffer = new Float32Array(this.frameSize);
    this._offset = 0;
  }

  process(inputs) {
    const input = inputs[0];
    if (!input || input.length === 0) {
      return true;
    }
    const channel = input[0];
    if (!channel || channel.length === 0) {
      return true;
    }
    for (let i = 0; i < channel.length; i += 1) {
      this._buffer[this._offset] = channel[i];
      this._offset += 1;
      if (this._offset >= this.frameSize) {
        this.port.postMessage(this._buffer, [this._buffer.buffer]);
        this._buffer = new Float32Array(this.frameSize);
        this._offset = 0;
      }
    }
    return true;
  }
}

registerProcessor("pcm-capture-processor", PcmCaptureProcessor);
`;

let cachedCaptureWorkletUrl = null;

function captureWorkletModuleUrl() {
  if (cachedCaptureWorkletUrl) {
    return cachedCaptureWorkletUrl;
  }
  const blob = new Blob([CAPTURE_WORKLET_SOURCE], { type: "application/javascript" });
  cachedCaptureWorkletUrl = URL.createObjectURL(blob);
  return cachedCaptureWorkletUrl;
}

export default class extends Controller {
  static targets = [
    "toggleButton",
    "settingsModal",
    "fontSizeButton",
    "fontColorButton",
    "fontColorDot",
    "sourceLanguageSelect",
    "microphoneSelect",
    "translationToggle",
    "languageSelect",
    "status",
    "error",
    "sourceOutput",
    "sourceEmpty",
    "debugOutput"
  ];

  static values = {
    sessionUrl: String,
    reconnectUrl: String,
    chunkMs: { type: Number, default: 100 },
    meetingId: Number,
    meetingToken: String,
    currentUserId: Number,
    speakerName: String,
    sourceLanguage: { type: String, default: "fr" },
    sourceLanguageMode: { type: String, default: "fixed" }
  };

  connect() {
    this.tabId = this.buildTabId();
    this.peerTargetLanguage = null;
    this.peerTranslationEnabled = false;
    this.isRestartingForLocalPreferences = false;
    this.isRestartingForPeerPreferences = false;
    this.isRestartingForMicrophoneChange = false;
    this.manualTargetLanguageSelected = false;
    this.manualSourceLanguageSelected = false;
    this.userToggledStreaming = false;
    this.automaticStreaming = false;
    this.autoPausedForSilence = false;
    this.transmissionSuspended = false;
    this.suspendedVoiceStreak = 0;
    this.preRollChunks = [];
    this.automaticStartInProgress = false;
    this.automaticStreamingEvaluationTimer = null;
    this.automaticStreamingEvaluationRunning = false;
    this.automaticStreamingEvaluationRequested = false;
    this.lastAutomaticBlockReason = "jitsi_not_joined";
    this.lastAutomaticStreamingError = "none";
    this.jitsiState = this.defaultJitsiState();
    this.meetingChannelConnected = false;
    this.subscribeToMeetingChannel();
    this.boundHandleDeviceChange = this.handleDeviceChange.bind(this);
    this.boundHandleKeydown = this.handleKeydown.bind(this);
    this.boundHandleJitsiState = this.handleJitsiState.bind(this);
    this.boundHandleWeglotLanguageChange = this.handleWeglotLanguageChange.bind(this);
    this.boundHandleWeglotLanguageClick = this.handleWeglotLanguageClick.bind(this);
    navigator.mediaDevices?.addEventListener?.("devicechange", this.boundHandleDeviceChange);
    document.addEventListener("keydown", this.boundHandleKeydown);
    document.addEventListener("live-captions:jitsi-state", this.boundHandleJitsiState);
    document.addEventListener("click", this.boundHandleWeglotLanguageClick, true);
    this.subscribeToWeglotLanguageChanges();
    this.resetState();
    this.loadCaptionAppearance();
    this.applyCaptionAppearance();
    this.loadPreferredMicrophoneId();
    this.refreshMicrophoneOptions();
    this.syncTargetLanguageFromWeglot();
    this.syncSourceLanguageFromWeglot();
    this.syncTranslationControls();
    this.renderSourceTranscript();
    this.renderTranslationTranscript();
    this.renderDebug();
    this.waitForWeglotLanguage();
    this.queueAutomaticStreamingEvaluation();
  }

  disconnect() {
    this.unsubscribeFromMeetingChannel();
    navigator.mediaDevices?.removeEventListener?.("devicechange", this.boundHandleDeviceChange);
    document.removeEventListener("keydown", this.boundHandleKeydown);
    document.removeEventListener("live-captions:jitsi-state", this.boundHandleJitsiState);
    document.removeEventListener("click", this.boundHandleWeglotLanguageClick, true);
    this.unsubscribeFromWeglotLanguageChanges();
    this.stopWaitingForWeglotLanguage();
    this.clearAutomaticStreamingEvaluation();
    this.teardown();
  }

  toggleTranslation() {
    this.applyTranslationPreferencesChange();
  }

  applyTranslationPreferencesChange({ forceRestart = false } = {}) {
    const shouldRestartStreaming = this.isRunning && (forceRestart || this.targetLanguagesChanged());
    this.autoPausedForSilence = false;

    this.resetRemoteTranslationBuffer();
    this.syncTranslationControls();
    this.renderTranslationTranscript();
    this.broadcastTranslationPreferences();
    this.renderDebug();
    this.queueAutomaticStreamingEvaluation();

    if (shouldRestartStreaming) {
      this.restartStreamingForLocalPreferences();
    }
  }

  targetLanguageChanged() {
    this.manualTargetLanguageSelected = true;
    const spokenLanguageSynced = this.syncSpokenLanguageFromTarget();
    this.applyTranslationPreferencesChange({ forceRestart: spokenLanguageSynced });
  }

  sourceLanguageChanged() {
    this.manualSourceLanguageSelected = true;
    this.syncTargetLanguageFromSpoken();
    const shouldRestartStreaming = this.isRunning;
    this.autoPausedForSilence = false;
    this.renderDebug();
    this.queueAutomaticStreamingEvaluation();

    if (shouldRestartStreaming) {
      this.restartStreamingForLocalPreferences("Mise a jour de la langue parlee...");
    }
  }

  // The bar pill and the settings select expose one single "my language" setting: the language the
  // user speaks is also the one they read captions in. Mirror pill -> settings; a pill on
  // "Détection automatique" says nothing about the reading language, so it leaves it untouched.
  syncTargetLanguageFromSpoken() {
    const selectedLanguage = this.selectedSourceLanguageValue;
    if (!selectedLanguage || selectedLanguage === "auto" || !this.hasLanguageSelectTarget ||
        !this.languageSelectHasValue(selectedLanguage) || this.languageSelectTarget.value === selectedLanguage) {
      return false;
    }

    this.manualTargetLanguageSelected = true;
    this.languageSelectTarget.value = selectedLanguage;
    this.resetRemoteTranslationBuffer();
    this.renderTranslationTranscript();
    this.broadcastTranslationPreferences();
    return true;
  }

  // Mirror settings -> pill. Skipped while the pill is on auto-detect: detection keeps working
  // whatever the reading language is, and the user chose it explicitly.
  syncSpokenLanguageFromTarget() {
    const selectedLanguage = this.hasLanguageSelectTarget ? this.languageSelectTarget.value : null;
    if (!selectedLanguage || !this.hasSourceLanguageSelectTarget ||
        this.sourceLanguageSelectTarget.value === "auto" ||
        !this.sourceLanguageSelectHasValue(selectedLanguage) ||
        this.sourceLanguageSelectTarget.value === selectedLanguage) {
      return false;
    }

    this.manualSourceLanguageSelected = true;
    this.sourceLanguageSelectTarget.value = selectedLanguage;
    return true;
  }

  async microphoneChanged() {
    const previousMicrophoneId = this.selectedMicrophoneId || "";
    this.selectedMicrophoneId = this.hasMicrophoneSelectTarget ? this.microphoneSelectTarget.value : "";
    this.persistPreferredMicrophoneId();
    this.renderDebug();

    try {
      await this.syncJitsiAudioInputDeviceByLabel(this.selectedMicrophoneLabel());
    } catch (error) {
      this.setError(error.message || "Impossible de synchroniser le micro avec Jitsi.");
      return;
    }

    await this.handleMicrophoneSelectionChange(previousMicrophoneId, "local");
  }

  handleDeviceChange() {
    this.refreshMicrophoneOptions();
  }

  handleKeydown(event) {
    if (event.key === "Escape" && this.settingsModalOpen()) {
      this.closeSettings();
    }
  }

  openSettings() {
    if (this.hasSettingsModalTarget) {
      this.settingsModalTarget.hidden = false;
    }
  }

  closeSettings() {
    if (this.hasSettingsModalTarget) {
      this.settingsModalTarget.hidden = true;
    }
  }

  settingsModalOpen() {
    return this.hasSettingsModalTarget && !this.settingsModalTarget.hidden;
  }

  loadCaptionAppearance() {
    this.captionFontSizeIndex = this.readStoredAppearanceIndex(CAPTION_FONT_SIZE_STORAGE_KEY, CAPTION_FONT_SIZES.length, 1);
    this.captionColorIndex = this.readStoredAppearanceIndex(CAPTION_COLOR_STORAGE_KEY, CAPTION_COLORS.length, 0);
  }

  readStoredAppearanceIndex(key, length, fallback) {
    try {
      const parsed = Number.parseInt(window.localStorage.getItem(key), 10);
      if (Number.isInteger(parsed) && parsed >= 0 && parsed < length) {
        return parsed;
      }
    } catch (_error) {
      // Ignore storage failures and use the fallback.
    }

    return fallback;
  }

  persistAppearanceIndex(key, value) {
    try {
      window.localStorage.setItem(key, String(value));
    } catch (_error) {
      // Ignore storage failures.
    }
  }

  cycleFontSize() {
    this.captionFontSizeIndex = (this.captionFontSizeIndex + 1) % CAPTION_FONT_SIZES.length;
    this.persistAppearanceIndex(CAPTION_FONT_SIZE_STORAGE_KEY, this.captionFontSizeIndex);
    this.applyCaptionAppearance();
  }

  cycleFontColor() {
    this.captionColorIndex = (this.captionColorIndex + 1) % CAPTION_COLORS.length;
    this.persistAppearanceIndex(CAPTION_COLOR_STORAGE_KEY, this.captionColorIndex);
    this.applyCaptionAppearance();
  }

  applyCaptionAppearance() {
    const fontSize = CAPTION_FONT_SIZES[this.captionFontSizeIndex] || CAPTION_FONT_SIZES[1];
    const color = CAPTION_COLORS[this.captionColorIndex] || CAPTION_COLORS[0];

    this.element.style.setProperty("--lc-caption-font-size", fontSize.value);
    this.element.style.setProperty("--lc-caption-color", color.value);

    if (this.hasFontSizeButtonTarget) {
      this.fontSizeButtonTarget.title = `Taille du texte : ${fontSize.label}`;
    }

    if (this.hasFontColorButtonTarget) {
      this.fontColorButtonTarget.title = `Couleur du texte : ${color.label}`;
    }

    if (this.hasFontColorDotTarget) {
      this.fontColorDotTarget.style.background = color.value;
    }
  }

  async handleJitsiState(event) {
    this.jitsiState = {
      ...this.defaultJitsiState(),
      ...(event.detail || {}),
      remoteAudioMuted: { ...(event.detail?.remoteAudioMuted || {}) }
    };

    await this.refreshMicrophoneOptions();
    await this.syncMicrophoneSelectionWithJitsi();
    this.queueAutomaticStreamingEvaluation();
    this.renderDebug();
  }

  defaultJitsiState() {
    return {
      conferenceJoined: false,
      participantCount: 0,
      localParticipantId: null,
      localAudioMuted: true,
      localAudioAvailable: true,
      remoteAudioMuted: {},
      currentAudioInputDevice: null,
      availableAudioInputDevices: [],
      reason: "initial"
    };
  }

  handleWeglotLanguageChange(language) {
    this.syncTargetLanguageFromWeglot(language);
    this.syncSourceLanguageFromWeglot(language);
  }

  handleWeglotLanguageClick(event) {
    const languageElement = event.target?.closest?.("[id^='weglot-language-']");
    const language = this.languageFromWeglotElement(languageElement);
    if (!language) {
      return;
    }

    window.setTimeout(() => {
      this.syncTargetLanguageFromWeglot(language);
      this.syncSourceLanguageFromWeglot(language);
    }, 0);
  }

  subscribeToWeglotLanguageChanges() {
    if (!window.Weglot || typeof window.Weglot.on !== "function") {
      return;
    }

    try {
      window.Weglot.on("languageChanged", this.boundHandleWeglotLanguageChange);
      this.weglotLanguageChangeSubscribed = true;
    } catch (_error) {
      this.weglotLanguageChangeSubscribed = false;
    }
  }

  waitForWeglotLanguage() {
    this.stopWaitingForWeglotLanguage();
    if (this.weglotLanguageChangeSubscribed || document.body?.dataset?.weglotEnabled !== "true") {
      return;
    }

    this.weglotWaitAttempts = 0;
    this.weglotWaitInterval = window.setInterval(() => {
      this.weglotWaitAttempts += 1;
      this.subscribeToWeglotLanguageChanges();
      this.syncTargetLanguageFromWeglot();
      this.syncSourceLanguageFromWeglot();

      if (this.weglotLanguageChangeSubscribed || this.weglotWaitAttempts >= 20) {
        this.stopWaitingForWeglotLanguage();
      }
    }, 250);
  }

  stopWaitingForWeglotLanguage() {
    if (this.weglotWaitInterval) {
      window.clearInterval(this.weglotWaitInterval);
      this.weglotWaitInterval = null;
    }
  }

  unsubscribeFromWeglotLanguageChanges() {
    if (!this.weglotLanguageChangeSubscribed || !window.Weglot || typeof window.Weglot.off !== "function") {
      return;
    }

    try {
      window.Weglot.off("languageChanged", this.boundHandleWeglotLanguageChange);
    } catch (_error) {
      // Ignore Weglot teardown failures.
    }
    this.weglotLanguageChangeSubscribed = false;
  }

  syncTargetLanguageFromWeglot(language = null) {
    if (this.manualTargetLanguageSelected) {
      return;
    }

    const weglotLanguage = language || this.currentWeglotLanguage();
    const deeplLanguage = this.deeplTargetLanguageForWeglotLanguage(weglotLanguage);
    if (!deeplLanguage || !this.hasLanguageSelectTarget || !this.languageSelectHasValue(deeplLanguage)) {
      return;
    }

    const languageChanged = this.languageSelectTarget.value !== deeplLanguage;
    const shouldRestartStreaming = this.isRunning && languageChanged;
    this.languageSelectTarget.value = deeplLanguage;
    this.lastWeglotLanguage = weglotLanguage;
    if (languageChanged) {
      // Drop tentative/orphan translations in the previous language so they can't attach to
      // lines produced under the new one; the DeepL session restart below takes over from here.
      this.resetRemoteTranslationBuffer();
      this.renderTranslationTranscript();
    }
    this.syncTranslationControls();
    this.broadcastTranslationPreferences();
    this.renderDebug();
    this.autoPausedForSilence = false;
    this.queueAutomaticStreamingEvaluation();

    if (shouldRestartStreaming) {
      this.restartStreamingForLocalPreferences();
    }
  }

  // Default the spoken-language dropdown to the viewer's Weglot language (French fallback comes
  // from the server-rendered default). "Détection automatique" stays selectable; once the user
  // picks any option manually, we stop overriding it.
  syncSourceLanguageFromWeglot(language = null) {
    if (this.manualSourceLanguageSelected) {
      return;
    }

    const weglotLanguage = language || this.currentWeglotLanguage();
    const deeplLanguage = this.deeplTargetLanguageForWeglotLanguage(weglotLanguage);
    if (!deeplLanguage || !this.hasSourceLanguageSelectTarget || !this.sourceLanguageSelectHasValue(deeplLanguage)) {
      return;
    }

    const shouldRestartStreaming = this.isRunning && this.sourceLanguageSelectTarget.value !== deeplLanguage;
    this.sourceLanguageSelectTarget.value = deeplLanguage;
    this.renderDebug();
    this.autoPausedForSilence = false;
    this.queueAutomaticStreamingEvaluation();

    if (shouldRestartStreaming) {
      this.restartStreamingForLocalPreferences("Mise a jour de la langue parlee...");
    }
  }

  sourceLanguageSelectHasValue(value) {
    return Array.from(this.sourceLanguageSelectTarget.options).some((option) => option.value === value);
  }

  currentWeglotLanguage() {
    if (window.Weglot && typeof window.Weglot.getCurrentLang === "function") {
      try {
        return window.Weglot.getCurrentLang();
      } catch (_error) {
        return this.currentWeglotLanguageFromDom();
      }
    }

    return this.currentWeglotLanguageFromDom();
  }

  currentWeglotLanguageFromDom() {
    const selectedLanguageElement = document.querySelector(
      "[id^='weglot-language-'][aria-current='true'], [id^='weglot-language-'].active, [id^='weglot-language-'].wgcurrent, [id^='weglot-language-'].weglot-active"
    );

    return this.languageFromWeglotElement(selectedLanguageElement);
  }

  languageFromWeglotElement(element) {
    if (!element?.id) {
      return null;
    }

    return element.id.replace(/^weglot-language-/, "") || null;
  }

  deeplTargetLanguageForWeglotLanguage(language) {
    const normalizedLanguage = this.normalizeLanguageTag(language);
    if (!normalizedLanguage) {
      return null;
    }

    return WEGLOT_TO_DEEPL_TARGET_LANGUAGE[normalizedLanguage] || WEGLOT_TO_DEEPL_TARGET_LANGUAGE[normalizedLanguage.split("-")[0]] || null;
  }

  languageSelectHasValue(value) {
    return Array.from(this.languageSelectTarget.options).some((option) => option.value === value);
  }

  queueAutomaticStreamingEvaluation() {
    if (this.automaticStreamingEvaluationTimer) {
      return;
    }

    this.automaticStreamingEvaluationTimer = window.setTimeout(() => {
      this.automaticStreamingEvaluationTimer = null;
      this.applyAutomaticStreamingState();
    }, 0);
  }

  clearAutomaticStreamingEvaluation() {
    if (this.automaticStreamingEvaluationTimer) {
      window.clearTimeout(this.automaticStreamingEvaluationTimer);
      this.automaticStreamingEvaluationTimer = null;
    }
  }

  async applyAutomaticStreamingState() {
    if (this.automaticStreamingEvaluationRunning) {
      this.automaticStreamingEvaluationRequested = true;
      return;
    }

    this.automaticStreamingEvaluationRunning = true;

    try {
      const blockReason = this.automaticStreamingBlockReason();
      this.lastAutomaticBlockReason = blockReason || "none";

      if (blockReason) {
        this.autoPausedForSilence = false;

        if (this.isRunning && this.automaticStreaming) {
          await this.stopStreaming({
            preserveTranscript: true,
            finalStatusMessage: this.automaticStatusMessage(blockReason),
            finalStatus: IDLE_STATUS
          });
        } else if (!this.isRunning && !this.userToggledStreaming) {
          this.setStatus(this.automaticStatusMessage(blockReason), IDLE_STATUS);
        }

        this.renderDebug();
        return;
      }

      if (this.isRunning || this.automaticStartInProgress) {
        return;
      }

      await this.startAutomaticStreaming();
    } catch (error) {
      this.lastAutomaticStreamingError = error.message || "automatic_streaming_error";
      this.renderDebug();
    } finally {
      this.automaticStreamingEvaluationRunning = false;
      if (this.automaticStreamingEvaluationRequested) {
        this.automaticStreamingEvaluationRequested = false;
        this.queueAutomaticStreamingEvaluation();
      }
    }
  }

  automaticStreamingBlockReason() {
    if (this.userToggledStreaming) {
      return "manual_override";
    }

    if (!this.jitsiState?.conferenceJoined) {
      return "jitsi_not_joined";
    }

    if ((this.jitsiState.participantCount || 0) < 2) {
      return "waiting_for_second_participant";
    }

    if (this.jitsiState.localAudioAvailable === false) {
      return "local_audio_unavailable";
    }

    if (this.jitsiState.localAudioMuted) {
      return "local_microphone_muted";
    }

    if (this.remoteAudioMuted()) {
      return "remote_microphone_muted";
    }

    return null;
  }

  automaticTranslationNeeded() {
    return true;
  }

  remoteAudioMuted() {
    return Object.values(this.jitsiState?.remoteAudioMuted || {}).some((isMuted) => Boolean(isMuted));
  }

  automaticStatusMessage(reason) {
    const messages = {
      manual_override: "Sous-titres en mode manuel.",
      jitsi_not_joined: "En attente de la visioconference.",
      waiting_for_second_participant: "En attente de l'interlocuteur.",
      local_audio_unavailable: "Micro indisponible dans Jitsi.",
      local_microphone_muted: "Micro coupe.",
      remote_microphone_muted: "Micro interlocuteur coupe."
    };

    return messages[reason] || "Sous-titres inactifs.";
  }

  async startAutomaticStreaming() {
    if (this.isRunning || this.automaticStartInProgress || this.automaticStreamingBlockReason()) {
      return;
    }

    this.automaticStartInProgress = true;

    try {
      await this.startStreaming({ preserveTranscript: true, automatic: true });
    } finally {
      this.automaticStartInProgress = false;
    }
  }

  async toggleStreaming(event) {
    event?.preventDefault();
    this.userToggledStreaming = true;
    this.autoPausedForSilence = false;
    this.automaticStreaming = false;
    if (this.isRunning) {
      await this.stopStreaming();
    } else {
      await this.startStreaming({ automatic: false });
    }
  }

  async startStreaming({ preserveTranscript = false, automatic = false } = {}) {
    if (this.isRunning) {
      return;
    }

    if (!this.browserIsSupported()) {
      this.setError("Ce navigateur ne supporte pas la capture micro requise. Pour ce test, utilisez Chrome ou Edge recents.");
      return;
    }

    if (!this.audioContextClass()) {
      this.setError("Ce navigateur ne supporte pas la capture audio Web requise. Pour ce test, utilisez Chrome ou Edge.");
      return;
    }

    this.clearMessages();
    if (!preserveTranscript) {
      this.resetTranscriptBuffers();
    }
    this.syncTranslationControls();
    this.renderSourceTranscript();
    this.renderTranslationTranscript();
    this.setStatus("Autorisation du microphone...", PENDING_STATUS);
    this.automaticStreaming = automatic;
    this.streamingStartedAt = null;
    this.lastVoiceDetectedAt = null;
    // In automatic mode the session opens before anyone speaks; start suspended so no audio is
    // sent until the first detected voice. Manual mode streams continuously.
    this.transmissionSuspended = automatic;
    this.suspendedVoiceStreak = 0;
    this.preRollChunks = [];

    try {
      this.selectedMicrophoneId = this.hasMicrophoneSelectTarget ? this.microphoneSelectTarget.value : this.selectedMicrophoneId;
      this.mediaStream = await this.requestMicrophoneStream();

      this.captureTrack = this.mediaStream.getAudioTracks()[0] || null;
      this.captureTrackSettings = this.captureTrack?.getSettings?.() || {};
      if (this.captureTrackSettings.deviceId) {
        this.selectedMicrophoneId = this.captureTrackSettings.deviceId;
        this.persistPreferredMicrophoneId();
      }
      await this.refreshMicrophoneOptions();

      await this.setupAudioPipeline();
      const requestedTargetLanguages = this.outgoingTargetLanguages;
      const session = await this.postJson(this.sessionUrlValue, this.buildSessionPayload(requestedTargetLanguages));
      this.currentToken = session.token;
      this.streamingUrl = session.streaming_url;
      this.sessionId = session.session_id;
      this.activeTargetLanguages = requestedTargetLanguages;
      this.intentionalStop = false;
      this.endOfSourceSent = false;
      this.reconnectAttempts = 0;
      this.receivedTerminalEvent = false;
      this.pendingChunks = [];
      this.streamingStartedAt = Date.now();
      this.lastVoiceDetectedAt = null;

      this.setStatus("Connexion a DeepL...", PENDING_STATUS);
      await this.openSocket(session.streaming_url, session.token);
      this.lastSocketEvent = "socket_open";
      this.isRunning = true;
      this.toggleControls(true);
      this.startRecorder();
      this.setStatus("Sous-titres en direct actifs.", LIVE_STATUS);
      this.renderDebug();

      if (this.targetLanguagesChanged()) {
        this.restartStreamingForLocalPreferences();
      }
    } catch (error) {
      this.setError(error.message || "Impossible de lancer les sous-titres.");
      this.teardown();
    }
  }

  normalizeLanguageTag(language) {
    return (language || "").toLowerCase();
  }

  buildSessionPayload(targetLanguages = this.outgoingTargetLanguages) {
    const payload = {
      source_language: this.currentSourceLanguage,
      source_language_mode: this.currentSourceLanguageMode,
      source_media_content_type: this.sourceMediaContentType,
      target_languages: targetLanguages
    };

    return payload;
  }

  browserIsSupported() {
    return Boolean(
      navigator.mediaDevices?.getUserMedia &&
      navigator.mediaDevices?.enumerateDevices &&
      window.WebSocket &&
      this.audioContextClass()
    );
  }

  audioContextClass() {
    return window.AudioContext || window.webkitAudioContext;
  }

  get translationEnabled() {
    return this.hasTranslationToggleTarget ? this.translationToggleTarget.checked : false;
  }

  get outgoingTargetLanguages() {
    const languages = [];

    if (this.peerTranslationEnabled && this.peerTargetLanguage && !this.sameBaseLanguage(this.peerTargetLanguage, this.currentSourceLanguage)) {
      languages.push(this.peerTargetLanguage);
    }

    if (this.translationEnabled && this.hasLanguageSelectTarget) {
      const localLang = this.languageSelectTarget.value;
      if (localLang && languages.indexOf(localLang) === -1 && !this.sameBaseLanguage(localLang, this.currentSourceLanguage)) {
        languages.push(localLang);
      }
    }

    return languages;
  }

  // Translating a language into itself ("es"->"es", or "en-US" speech read in "en-GB") only
  // produces a noisy near-copy of the source transcript whose fragments then have to re-attach
  // to their source lines — every miss shows as a duplicated phrase. Skip requesting those
  // targets; the source transcript is already in the viewer's language. Auto-detected speech
  // can't be compared, so it always keeps its translations.
  sameBaseLanguage(leftLanguage, rightLanguage) {
    const leftBase = this.normalizeLanguageTag(leftLanguage).split("-")[0];
    const rightBase = this.normalizeLanguageTag(rightLanguage).split("-")[0];
    return Boolean(leftBase) && Boolean(rightBase) && leftBase === rightBase;
  }

  get selectedSourceLanguageValue() {
    if (this.hasSourceLanguageSelectTarget) {
      return this.sourceLanguageSelectTarget.value;
    }

    return this.sourceLanguageModeValue === "auto" ? "auto" : this.sourceLanguageValue;
  }

  get currentSourceLanguageMode() {
    return this.selectedSourceLanguageValue === "auto" ? "auto" : "fixed";
  }

  get currentSourceLanguage() {
    return this.currentSourceLanguageMode === "fixed" ? this.selectedSourceLanguageValue : null;
  }

  syncTranslationControls() {
    if (this.hasLanguageSelectTarget) {
      this.languageSelectTarget.disabled = !this.translationEnabled;
    }
  }

  toggleControls(running) {
    if (this.hasToggleButtonTarget) {
      this.toggleButtonTarget.dataset.running = running ? "true" : "false";
      this.toggleButtonTarget.textContent = running ? "Arreter" : "Lancer";
    }
    this.sourceLanguageSelectTarget.disabled = false;
    if (this.hasMicrophoneSelectTarget) {
      this.microphoneSelectTarget.disabled = false;
    }
    this.translationToggleTarget.disabled = false;
    this.languageSelectTarget.disabled = !this.translationEnabled;
  }

  loadPreferredMicrophoneId() {
    try {
      this.selectedMicrophoneId = window.localStorage.getItem(MICROPHONE_STORAGE_KEY) || "";
    } catch (_error) {
      this.selectedMicrophoneId = "";
    }
  }

  persistPreferredMicrophoneId() {
    try {
      if (this.selectedMicrophoneId) {
        window.localStorage.setItem(MICROPHONE_STORAGE_KEY, this.selectedMicrophoneId);
      } else {
        window.localStorage.removeItem(MICROPHONE_STORAGE_KEY);
      }
    } catch (_error) {
      // Ignore storage failures.
    }
  }

  async refreshMicrophoneOptions() {
    if (!this.hasMicrophoneSelectTarget) {
      return;
    }

    const devices = navigator.mediaDevices?.enumerateDevices
      ? await navigator.mediaDevices.enumerateDevices().catch(() => [])
      : [];
    const microphones = devices.filter((device) => device.kind === "audioinput");
    this.browserAudioInputDevices = microphones;
    const currentJitsiDevice = this.currentJitsiAudioInputDevice();
    const fallbackOptionLabel = microphones.length === 0 ? "Aucun microphone detecte" : "Microphone par defaut";
    const currentBrowserDevice = this.browserAudioInputDeviceForLabel(currentJitsiDevice?.label || "");
    const currentValue = currentBrowserDevice?.deviceId || this.selectedMicrophoneId || this.microphoneSelectTarget.value || "";
    const optionsSignature = [
      `fallback:${fallbackOptionLabel}`,
      ...microphones.map((device) => `${device.deviceId || ""}:${device.label || ""}`)
    ].join("|");

    if (this.lastRenderedMicrophoneOptionsSignature !== optionsSignature) {
      this.microphoneSelectTarget.innerHTML = "";
      this.microphoneSelectTarget.append(this.buildOption("", fallbackOptionLabel));

      microphones.forEach((device, index) => {
        const label = device.label || `Microphone ${index + 1}`;
        this.microphoneSelectTarget.append(this.buildOption(device.deviceId, label));
      });

      this.lastRenderedMicrophoneOptionsSignature = optionsSignature;
    }

    const hasCurrentValue = microphones.some((device) => device.deviceId === currentValue);
    this.microphoneSelectTarget.value = hasCurrentValue ? currentValue : "";
    this.selectedMicrophoneId = this.microphoneSelectTarget.value;
    this.persistPreferredMicrophoneId();
    this.renderDebug();
  }

  availableJitsiAudioInputDevices() {
    return Array.isArray(this.jitsiState?.availableAudioInputDevices)
      ? this.jitsiState.availableAudioInputDevices.filter((device) => device && device.kind === "audioinput")
      : [];
  }

  currentJitsiAudioInputDevice() {
    const currentDevice = this.jitsiState?.currentAudioInputDevice;
    if (!currentDevice) {
      return null;
    }

    return {
      deviceId: currentDevice.deviceId || "",
      groupId: currentDevice.groupId || "",
      kind: currentDevice.kind || "audioinput",
      label: currentDevice.label || ""
    };
  }

  async syncMicrophoneSelectionWithJitsi() {
    const currentJitsiDevice = this.currentJitsiAudioInputDevice();
    if (!currentJitsiDevice) {
      return;
    }

    const previousMicrophoneId = this.selectedMicrophoneId || "";
    const nextMicrophoneId = this.browserAudioInputDeviceForLabel(currentJitsiDevice.label || "")?.deviceId || "";

    if (this.hasMicrophoneSelectTarget) {
      const hasOption = Array.from(this.microphoneSelectTarget.options).some((option) => option.value === nextMicrophoneId);
      this.microphoneSelectTarget.value = hasOption ? nextMicrophoneId : "";
    }

    this.selectedMicrophoneId = nextMicrophoneId;
    this.persistPreferredMicrophoneId();
    this.renderDebug();

    await this.handleMicrophoneSelectionChange(previousMicrophoneId, "jitsi");
  }

  async handleMicrophoneSelectionChange(previousMicrophoneId, source = "local") {
    if ((previousMicrophoneId || "") === (this.selectedMicrophoneId || "")) {
      return;
    }

    if (!this.isRunning) {
      return;
    }

    await this.restartStreamingForMicrophoneChange(source);
  }

  async restartStreamingForMicrophoneChange(source = "local") {
    if (
      !this.isRunning ||
      this.isRestartingForMicrophoneChange ||
      this.isRestartingForLocalPreferences ||
      this.isRestartingForPeerPreferences
    ) {
      return;
    }

    this.isRestartingForMicrophoneChange = true;
    this.setStatus(source === "jitsi" ? "Synchronisation du micro Jitsi..." : "Mise a jour du microphone...", PENDING_STATUS);
    const restartAutomaticStreaming = this.automaticStreaming;

    try {
      await this.stopStreaming({ preserveTranscript: true, finalStatusMessage: null });
      if (!this.element.isConnected) {
        return;
      }
      await this.startStreaming({ preserveTranscript: true, automatic: restartAutomaticStreaming });
    } finally {
      this.isRestartingForMicrophoneChange = false;
    }
  }

  jitsiApi() {
    return window.liveCaptionsJitsiApi || null;
  }

  async syncJitsiAudioInputDeviceByLabel(label) {
    const api = this.jitsiApi();
    if (!api || typeof api.setAudioInputDevice !== "function") {
      return;
    }

    const jitsiDevice = this.jitsiAudioInputDeviceForLabel(label);
    await Promise.resolve(api.setAudioInputDevice(jitsiDevice?.label || label || "", jitsiDevice?.deviceId || ""));
  }

  buildAudioConstraints(deviceId) {
    return {
      channelCount: 1,
      echoCancellation: true,
      noiseSuppression: true,
      autoGainControl: true,
      ...(deviceId ? { deviceId: { exact: deviceId } } : {})
    };
  }

  async requestMicrophoneStream() {
    const preferredDeviceId = this.selectedMicrophoneId || "";

    try {
      return await navigator.mediaDevices.getUserMedia({
        audio: this.buildAudioConstraints(preferredDeviceId)
      });
    } catch (error) {
      const missingPreferredDevice = preferredDeviceId && ["NotFoundError", "OverconstrainedError"].includes(error?.name);
      if (!missingPreferredDevice) {
        throw error;
      }

      this.selectedMicrophoneId = "";
      this.persistPreferredMicrophoneId();
      if (this.hasMicrophoneSelectTarget) {
        this.microphoneSelectTarget.value = "";
      }

      return navigator.mediaDevices.getUserMedia({
        audio: this.buildAudioConstraints("")
      });
    }
  }

  browserAudioInputDeviceForLabel(label) {
    const normalizedLabel = this.normalizeDeviceLabel(label);
    if (!normalizedLabel) {
      return null;
    }

    return (this.browserAudioInputDevices || []).find((device) => this.deviceLabelsMatch(device.label, normalizedLabel)) || null;
  }

  jitsiAudioInputDeviceForLabel(label) {
    const normalizedLabel = this.normalizeDeviceLabel(label);
    if (!normalizedLabel) {
      return null;
    }

    return this.availableJitsiAudioInputDevices().find((device) => this.deviceLabelsMatch(device.label, normalizedLabel)) || null;
  }

  normalizeDeviceLabel(label) {
    return (label || "").trim().toLowerCase();
  }

  deviceLabelsMatch(leftLabel, rightLabel) {
    const normalizedLeft = this.normalizeDeviceLabel(leftLabel);
    const normalizedRight = this.normalizeDeviceLabel(rightLabel);

    if (!normalizedLeft || !normalizedRight) {
      return false;
    }

    return normalizedLeft === normalizedRight ||
      normalizedLeft.includes(normalizedRight) ||
      normalizedRight.includes(normalizedLeft);
  }

  buildOption(value, label) {
    const option = document.createElement("option");
    option.value = value;
    option.textContent = label;
    return option;
  }

  clearMessages() {
    this.errorTarget.textContent = "";
    this.errorTarget.hidden = true;
  }

  setStatus(message, status) {
    this.statusTarget.textContent = message;
    this.statusTarget.dataset.state = status;
  }

  setError(message) {
    this.errorTarget.textContent = message;
    this.errorTarget.hidden = false;
    this.setStatus("Sous-titres inactifs.", ERROR_STATUS);
  }

  resetState() {
    this.isRunning = false;
    this.intentionalStop = false;
    this.endOfSourceSent = false;
    this.receivedTerminalEvent = false;
    this.reconnectAttempts = 0;
    this.pendingChunks = [];
    this.activeTargetLanguages = [];
    this.mediaStream = null;
    this.selectedMicrophoneId = this.selectedMicrophoneId || "";
    this.captureTrack = null;
    this.captureTrackSettings = {};
    this.audioContext = null;
    this.mediaStreamSource = null;
    this.processorNode = null;
    this.silentGainNode = null;
    this.socket = null;
    this.firstChunkTimeout = null;
    this.chunkKeepaliveInterval = null;
    this.currentToken = null;
    this.streamingUrl = null;
    this.sessionId = null;
    this.finalizationResolver = null;
    this.streamingStartedAt = null;
    this.lastVoiceDetectedAt = null;
    this.lastChunkSentAt = null;
    this.chunksProducedCount = 0;
    this.chunksSentCount = 0;
    this.sourceMediaContentType = null;
    this.recorderMimeType = null;
    this.audioSampleRate = null;
    this.targetAudioSampleRate = null;
    this.pcmSamplesPerChunk = 0;
    this.pcmSampleBuffer = [];
    this.lastChunkByteSize = 0;
    this.lastChunkRms = 0;
    this.maxChunkRms = 0;
    this.lastRawChunkRms = 0;
    this.maxRawChunkRms = 0;
    this.chunksWithSignalCount = 0;
    this.silenceChunkStreak = 0;
    this.lastSocketEvent = "none";
    this.lastSocketError = "none";
    this.transcriptMessageCount = 0;
    this.hallucinationDropCount = 0;
    this.resetTranscriptBuffers();
    this.toggleControls(false);
    this.setStatus("Sous-titres inactifs.", IDLE_STATUS);
  }

  resetTranscriptBuffers() {
    this.nextTranscriptLineId = 1;
    this.phraseBoundaryPending = false;
    // Per-speaker id of the last source line that received a translation; attachment never goes
    // backwards past it.
    this.translationAttachCursors = {};
    this.translationAttachMinLineId = 0;
    this.sourceTranscript = {
      recentLines: [],
      tentativeText: "",
      tentativeSpeakerTabId: null,
      tentativeSpeakerId: null,
      tentativeSpeakerName: ""
    };
    this.translationTranscript = {
      recentLines: [],
      tentativeText: "",
      tentativeSpeakerTabId: null,
      tentativeSpeakerId: null,
      tentativeSpeakerName: "",
      language: null
    };
  }

  async stopStreaming({ preserveTranscript = true, finalStatusMessage = "Sous-titres arretes.", finalStatus = IDLE_STATUS } = {}) {
    if (!this.isRunning && !this.socket && !this.mediaStream) {
      return;
    }

    this.intentionalStop = true;
    this.setStatus("Arret des sous-titres...", PENDING_STATUS);

    try {
      await this.stopRecorder();
      await this.sendEndOfSourceMedia();
      await this.waitForFinalization();
    } catch (error) {
      this.setError(error.message || "Erreur pendant l'arret des sous-titres.");
    } finally {
      this.teardown({ preserveTranscript });
      if (finalStatusMessage) {
        this.setStatus(finalStatusMessage, finalStatus);
      }
    }
  }

  teardown({ preserveTranscript = true } = {}) {
    if (this.socket) {
      this.socket.onopen = null;
      this.socket.onmessage = null;
      this.socket.onerror = null;
      this.socket.onclose = null;
      if (this.socket.readyState === WebSocket.OPEN || this.socket.readyState === WebSocket.CONNECTING) {
        this.socket.close();
      }
    }

    if (this.mediaStream) {
      this.mediaStream.getTracks().forEach((track) => track.stop());
    }

    if (this.processorNode) {
      this.processorNode.onaudioprocess = null;
      if (this.processorNode.port) {
        this.processorNode.port.onmessage = null;
      }
      this.processorNode.disconnect();
    }

    if (this.mediaStreamSource) {
      this.mediaStreamSource.disconnect();
    }

    if (this.silentGainNode) {
      this.silentGainNode.disconnect();
    }

    if (this.audioContext && this.audioContext.state !== "closed") {
      this.audioContext.close().catch(() => {});
    }

    this.mediaStream = null;
    this.captureTrack = null;
    this.captureTrackSettings = {};
    this.audioContext = null;
    this.mediaStreamSource = null;
    this.processorNode = null;
    this.silentGainNode = null;
    this.socket = null;
    this.pendingChunks = [];
    this.isRunning = false;
    this.automaticStreaming = false;
    this.transmissionSuspended = false;
    this.suspendedVoiceStreak = 0;
    this.preRollChunks = [];
    this.intentionalStop = false;
    this.endOfSourceSent = false;
    this.receivedTerminalEvent = false;
    this.reconnectAttempts = 0;
    this.clearFirstChunkWatchdog();
    this.currentToken = null;
    this.streamingUrl = null;
    this.finalizationResolver = null;
    this.lastChunkSentAt = null;
    this.stopChunkKeepalive();
    this.renderDebug();
    this.toggleControls(false);
    this.syncTranslationControls();

    if (!preserveTranscript) {
      this.resetTranscriptBuffers();
      this.renderSourceTranscript();
      this.renderTranslationTranscript();
    }
  }

  async setupAudioPipeline() {
    const AudioContextClass = this.audioContextClass();
    this.audioContext = this.createCaptureAudioContext(AudioContextClass);
    if (this.audioContext.state === "suspended") {
      await this.audioContext.resume();
    }

    this.audioSampleRate = this.audioContext.sampleRate;
    this.targetAudioSampleRate = this.preferredDeepLSampleRate(this.audioSampleRate);
    this.pcmSamplesPerChunk = Math.max(1, Math.round((this.targetAudioSampleRate * this.chunkMsValue) / 1000));
    this.pcmSampleBuffer = [];
    this.sourceMediaContentType = `audio/pcm;encoding=s16le;rate=${this.targetAudioSampleRate}`;
    this.recorderMimeType = this.sourceMediaContentType;

    this.mediaStreamSource = this.audioContext.createMediaStreamSource(this.mediaStream);
    this.silentGainNode = this.audioContext.createGain();
    this.silentGainNode.gain.value = 0;

    await this.setupCaptureNode();

    this.mediaStreamSource.connect(this.processorNode);
    this.processorNode.connect(this.silentGainNode);
    this.silentGainNode.connect(this.audioContext.destination);
    this.renderDebug();
  }

  // Capture at DeepL's recommended 16 kHz so the browser's high-quality resampler does the
  // downsampling. Some browsers reject a forced sample rate, so fall back to the device
  // default and rely on resampleChannelData().
  createCaptureAudioContext(AudioContextClass) {
    try {
      return new AudioContextClass({ sampleRate: PREFERRED_DEEPL_SAMPLE_RATE, latencyHint: "interactive" });
    } catch (_error) {
      return new AudioContextClass({ latencyHint: "interactive" });
    }
  }

  // Prefer an AudioWorklet (audio render thread) so capture is not starved by the heavy
  // videoconference UI on the main thread. Falls back to the deprecated ScriptProcessorNode
  // when AudioWorklet is unavailable.
  async setupCaptureNode() {
    if (await this.tryCreateWorkletCaptureNode()) {
      return;
    }
    this.createScriptProcessorCaptureNode();
  }

  async tryCreateWorkletCaptureNode() {
    if (!this.audioContext.audioWorklet || typeof AudioWorkletNode === "undefined") {
      return false;
    }

    try {
      await this.audioContext.audioWorklet.addModule(captureWorkletModuleUrl());
      const node = new AudioWorkletNode(this.audioContext, "pcm-capture-processor", {
        numberOfInputs: 1,
        numberOfOutputs: 1,
        channelCount: 1,
        processorOptions: { frameSize: WORKLET_FRAME_SIZE }
      });
      node.port.onmessage = (event) => {
        try {
          this.processCaptureSamples(event.data);
        } catch (_error) {
          this.setError("Impossible de traiter l'audio capture.");
          this.stopStreaming();
        }
      };
      this.processorNode = node;
      return true;
    } catch (_error) {
      return false;
    }
  }

  createScriptProcessorCaptureNode() {
    const node = this.audioContext.createScriptProcessor(PCM_BUFFER_SIZE, 1, 1);
    node.onaudioprocess = (event) => {
      try {
        this.handleAudioProcess(event);
      } catch (_error) {
        this.setError("Impossible de traiter l'audio capture.");
        this.stopStreaming();
      }
    };
    this.processorNode = node;
  }

  startRecorder() {
    this.startFirstChunkWatchdog();
    this.startChunkKeepalive();
  }

  async stopRecorder() {
    this.flushPcmSampleBuffer();
    this.stopChunkKeepalive();

    if (this.processorNode) {
      this.processorNode.onaudioprocess = null;
      if (this.processorNode.port) {
        this.processorNode.port.onmessage = null;
      }
      this.processorNode.disconnect();
      this.processorNode = null;
    }

    if (this.mediaStreamSource) {
      this.mediaStreamSource.disconnect();
      this.mediaStreamSource = null;
    }

    if (this.silentGainNode) {
      this.silentGainNode.disconnect();
      this.silentGainNode = null;
    }

    if (this.audioContext && this.audioContext.state !== "closed") {
      await this.audioContext.close().catch(() => {});
    }
    this.audioContext = null;
  }

  // ScriptProcessorNode fallback entry point; the AudioWorklet path calls
  // processCaptureSamples() directly with the posted Float32 frame.
  handleAudioProcess(event) {
    this.processCaptureSamples(event.inputBuffer.getChannelData(0));
  }

  processCaptureSamples(channelData) {
    if (!this.isRunning) {
      return;
    }

    if (!channelData || channelData.length === 0) {
      return;
    }

    const rawChunkRms = this.calculateFloatRms(channelData);
    this.lastRawChunkRms = rawChunkRms;
    this.maxRawChunkRms = Math.max(this.maxRawChunkRms, rawChunkRms);

    const resampledChannelData = this.resampleChannelData(channelData, this.audioSampleRate, this.targetAudioSampleRate);
    for (let index = 0; index < resampledChannelData.length; index += 1) {
      this.pcmSampleBuffer.push(this.floatTo16BitSample(resampledChannelData[index]));
    }

    while (this.pcmSampleBuffer.length >= this.pcmSamplesPerChunk) {
      const chunk = this.pcmSampleBuffer.splice(0, this.pcmSamplesPerChunk);
      this.publishPcmChunk(chunk);
    }
  }

  flushPcmSampleBuffer() {
    if (!this.pcmSampleBuffer.length) {
      return;
    }

    const chunk = this.pcmSampleBuffer.splice(0, this.pcmSampleBuffer.length);
    this.publishPcmChunk(chunk);
  }

  publishPcmChunk(samples) {
    if (!samples.length) {
      return;
    }

    const chunkRms = this.calculateInt16Rms(samples);
    const voiceDetected = chunkRms >= VOICE_ACTIVITY_THRESHOLD;
    this.lastChunkRms = chunkRms;
    this.maxChunkRms = Math.max(this.maxChunkRms, chunkRms);

    if (voiceDetected) {
      this.lastVoiceDetectedAt = Date.now();
      this.chunksWithSignalCount += 1;
      this.silenceChunkStreak = 0;
    } else {
      this.silenceChunkStreak += 1;
    }

    this.transmitOrSuspendChunk(samples, voiceDetected);

    this.chunksProducedCount += 1;
    this.clearFirstChunkWatchdog();
    this.lastChunkByteSize = samples.length * 2;

    if (this.chunksProducedCount % 5 === 0) {
      this.renderDebug();
    }

    if (this.chunksProducedCount === 1) {
      this.setStatus("Micro detecte, transcription en cours...", LIVE_STATUS);
      this.renderDebug();
    }
  }

  // Keep the DeepL session open but stop sending audio during silence (saving tokens) and resume
  // on voice, replaying a short pre-roll so onsets aren't clipped. Manual mode always sends.
  transmitOrSuspendChunk(samples, voiceDetected) {
    if (!this.automaticStreaming) {
      this.sendPcmChunk(samples);
      return;
    }

    if (this.transmissionSuspended) {
      // Buffer every chunk (incl. the voiced onset) so replay on resume isn't clipped; keepalive
      // keeps the session alive meanwhile.
      this.pushPreRollChunk(samples);

      if (voiceDetected) {
        this.suspendedVoiceStreak = (this.suspendedVoiceStreak || 0) + 1;
        this.suspendedSilenceRun = 0;
      } else {
        this.suspendedSilenceRun = (this.suspendedSilenceRun || 0) + 1;
        // Tolerate brief dips inside a word; only zero the count after sustained silence, so quiet
        // or choppy speech isn't stranded suspended and dropped.
        if (this.suspendedSilenceRun >= SUSPEND_RESET_SILENCE_CHUNKS) {
          this.suspendedVoiceStreak = 0;
        }
      }

      // Resume once enough voice has accumulated (transient blips never reach the threshold).
      // resumeTransmission() replays the buffered onset.
      if (this.suspendedVoiceStreak >= AUTO_SPEECH_START_CHUNKS) {
        this.resumeTransmission();
      }
      return;
    }

    if (voiceDetected) {
      this.sendPcmChunk(samples);
      return;
    }

    const silenceReferenceTime = this.lastVoiceDetectedAt || this.streamingStartedAt;
    if (silenceReferenceTime && Date.now() - silenceReferenceTime >= SILENCE_SUSPEND_MS) {
      this.suspendTransmission(samples);
      return;
    }

    // Still within the trailing hangover window: keep sending so trailing words aren't cut.
    this.sendPcmChunk(samples);
  }

  resumeTransmission() {
    this.transmissionSuspended = false;
    this.suspendedVoiceStreak = 0;
    this.suspendedSilenceRun = 0;
    // We just resumed after a silence-triggered suspension, i.e. a real pause: the next concluded
    // segment is a new phrase, so break the caption line rather than stitching it onto the last one.
    this.phraseBoundaryPending = true;
    this.preRollChunks.forEach((chunk) => this.sendPcmChunk(chunk));
    this.preRollChunks = [];
    this.setStatus("Sous-titres en direct actifs.", LIVE_STATUS);
    this.renderDebug();
  }

  suspendTransmission(samples) {
    this.transmissionSuspended = true;
    this.suspendedVoiceStreak = 0;
    this.suspendedSilenceRun = 0;
    this.preRollChunks = [];
    this.pushPreRollChunk(samples);
    this.setStatus("A l'ecoute... parlez pour reprendre les sous-titres.", LIVE_STATUS);
    this.renderDebug();
  }

  pushPreRollChunk(samples) {
    this.preRollChunks.push(samples);
    // Keep at least a full resume window of history, else the onset chunks counted toward
    // AUTO_SPEECH_START_CHUNKS could be evicted before resumeTransmission() replays them.
    const cap = Math.max(PREROLL_CHUNK_LIMIT, AUTO_SPEECH_START_CHUNKS + SUSPEND_RESET_SILENCE_CHUNKS);
    while (this.preRollChunks.length > cap) {
      this.preRollChunks.shift();
    }
  }

  sendPcmChunk(samples) {
    const base64Chunk = this.int16SamplesToBase64(samples);
    this.lastChunkSentAt = Date.now();
    this.enqueueOrSendChunk(base64Chunk);
  }

  startChunkKeepalive() {
    this.stopChunkKeepalive();
    this.chunkKeepaliveInterval = window.setInterval(() => {
      this.sendKeepaliveChunkIfNeeded();
    }, DEEPL_CHUNK_KEEPALIVE_INTERVAL_MS);
  }

  stopChunkKeepalive() {
    if (this.chunkKeepaliveInterval) {
      window.clearInterval(this.chunkKeepaliveInterval);
      this.chunkKeepaliveInterval = null;
    }
  }

  sendKeepaliveChunkIfNeeded() {
    if (!this.isRunning || !this.socket || this.socket.readyState !== WebSocket.OPEN) {
      return;
    }

    if (!this.lastChunkSentAt || Date.now() - this.lastChunkSentAt < DEEPL_CHUNK_KEEPALIVE_MAX_SILENCE_MS) {
      return;
    }

    this.sendPcmChunk(this.buildKeepaliveSilentChunk());
  }

  buildKeepaliveSilentChunk() {
    const sampleCount = Math.max(1, this.pcmSamplesPerChunk || 1);
    return new Int16Array(sampleCount);
  }

  enqueueOrSendChunk(base64Data) {
    if (this.socket?.readyState === WebSocket.OPEN) {
      this.socket.send(JSON.stringify({ source_media_chunk: { data: base64Data } }));
      this.chunksSentCount += 1;
      if (this.chunksSentCount % 10 === 0) {
        this.renderDebug();
      }
      return;
    }

    this.pendingChunks.push(base64Data);
    if (this.pendingChunks.length > 20) {
      this.pendingChunks.shift();
    }
  }

  flushPendingChunks() {
    if (!this.socket || this.socket.readyState !== WebSocket.OPEN) {
      return;
    }

    this.pendingChunks.forEach((chunk) => {
      this.socket.send(JSON.stringify({ source_media_chunk: { data: chunk } }));
      this.chunksSentCount += 1;
    });
    this.pendingChunks = [];
  }

  async sendEndOfSourceMedia() {
    if (!this.socket || this.socket.readyState !== WebSocket.OPEN || this.endOfSourceSent || this.receivedTerminalEvent) {
      return;
    }

    this.socket.send(JSON.stringify({ end_of_source_media: {} }));
    this.endOfSourceSent = true;
  }

  waitForFinalization() {
    if (this.receivedTerminalEvent || !this.socket || this.socket.readyState !== WebSocket.OPEN) {
      return Promise.resolve();
    }

    return new Promise((resolve) => {
      this.finalizationResolver = () => {
        resolve();
        this.finalizationResolver = null;
      };
      window.setTimeout(() => {
        resolve();
        this.finalizationResolver = null;
      }, FINALIZATION_TIMEOUT_MS);
    });
  }

  openSocket(streamingUrl, token) {
    return new Promise((resolve, reject) => {
      const socket = new WebSocket(`${streamingUrl}?token=${encodeURIComponent(token)}`);
      let resolved = false;

      socket.onopen = () => {
        resolved = true;
        this.socket = socket;
        this.flushPendingChunks();
        resolve();
      };

      socket.onmessage = async (event) => {
        this.lastSocketEvent = "message";
        try {
          await this.handleSocketMessage(event);
        } catch (error) {
          this.lastSocketError = error.message || "Message DeepL illisible.";
          this.renderDebug();
        }
      };

      socket.onerror = () => {
        this.lastSocketError = "socket_error";
        this.renderDebug();
        if (!resolved) {
          reject(new Error("Impossible d'ouvrir la connexion DeepL."));
        }
      };

      socket.onclose = (event) => {
        this.lastSocketEvent = `close:${event.code}`;
        this.lastSocketError = event.reason || this.lastSocketError;
        this.renderDebug();
        if (!resolved) {
          reject(new Error("La connexion DeepL s'est fermee avant d'etre prete."));
          return;
        }

        if (!this.intentionalStop && !this.receivedTerminalEvent) {
          this.handleUnexpectedClose(event);
        }
      };
    });
  }

  async handleUnexpectedClose(event) {
    if (this.reconnectAttempts >= MAX_RECONNECT_ATTEMPTS || !this.currentToken) {
      const closeDetail = event?.reason ? ` (${event.reason})` : "";
      this.setError(`Connexion DeepL interrompue. Relancez les sous-titres.${closeDetail}`);
      this.teardown({ preserveTranscript: true });
      return;
    }

    this.reconnectAttempts += 1;
    this.setStatus("Connexion interrompue, tentative de reconnexion...", PENDING_STATUS);
    await this.delay(RECONNECT_BACKOFF_MS * this.reconnectAttempts);
    if (!this.isRunning) {
      return;
    }

    try {
      const session = await this.postJson(this.reconnectUrlValue, { token: this.currentToken });
      this.currentToken = session.token;
      this.streamingUrl = session.streaming_url;
      await this.openSocket(session.streaming_url, session.token);
      this.setStatus("Connexion DeepL retablie.", LIVE_STATUS);
    } catch (error) {
      try {
        this.setStatus("Session DeepL expiree, ouverture d'une nouvelle session...", PENDING_STATUS);
        const requestedTargetLanguages = this.outgoingTargetLanguages;
        const session = await this.postJson(this.sessionUrlValue, this.buildSessionPayload(requestedTargetLanguages));
        this.currentToken = session.token;
        this.streamingUrl = session.streaming_url;
        this.sessionId = session.session_id;
        await this.openSocket(session.streaming_url, session.token);
        this.activeTargetLanguages = requestedTargetLanguages;
        this.setStatus("Nouvelle session DeepL ouverte.", LIVE_STATUS);
      } catch (sessionError) {
        this.setError(sessionError.message || error.message || "Echec de reconnexion a DeepL.");
        this.teardown({ preserveTranscript: true });
      }
    }
  }

  delay(ms) {
    return new Promise((resolve) => window.setTimeout(resolve, ms));
  }

  async handleSocketMessage(event) {
    const payload = await this.decodeSocketPayload(event.data);
    const data = JSON.parse(payload);
    this.lastSocketEvent = Object.keys(data)[0] || "unknown";
    // The connection is healthy again: refill the reconnect budget so the next idle timeout can
    // also be recovered transparently instead of forcing a manual relaunch.
    this.reconnectAttempts = 0;
    this.renderDebug();

    // DeepL hallucinates plausible filler ("C'est la vie.") from silence, and repeats it for as
    // long as the silence lasts. Real transcripts trail real speech by a couple of seconds at
    // most, so anything arriving this long after the last detected voice is discarded — for the
    // local display and the relay alike — EXCEPT an update that concludes words already shown as
    // tentative: that is the (legitimately late) finalization of real trailing speech, while
    // hallucinations introduce new text. While the user is speaking, lastVoiceDetectedAt
    // refreshes continuously and nothing real is ever gated.
    if ((data.source_transcript_update || data.target_transcript_update) &&
        this.isLikelySilenceHallucination() && !this.finalizesVisibleTentative(data)) {
      this.hallucinationDropCount = (this.hallucinationDropCount || 0) + 1;
      // DeepL has moved on to hallucinating new content, so a still-displayed tentative from
      // real speech would have been concluded by now — clear it instead of letting it linger.
      this.discardStaleTentative();
      this.renderDebug();
      return;
    }

    if (data.source_transcript_update) {
      this.transcriptMessageCount += 1;
      const localMeta = { speakerTabId: this.tabId, speakerId: this.currentUserIdValue, speakerName: this.speakerNameValue };
      const sourceLine = this.applyTranscriptUpdate(this.sourceTranscript, data.source_transcript_update, localMeta);
      this.renderSourceTranscript();
      this.broadcastTranscriptUpdate("source", data.source_transcript_update, null, { sourceLineId: sourceLine?.sourceLineId });
      this.renderDebug();
      return;
    }

    if (data.target_transcript_update) {
      this.transcriptMessageCount += 1;
      const localMeta = { speakerTabId: this.tabId, speakerId: this.currentUserIdValue, speakerName: this.speakerNameValue };
      const updateLanguage = data.target_transcript_update.language || null;

      // DeepL echoes every requested target language back on our own socket, including the language
      // we requested purely so the peer receives their translation. Only merge/display the one that
      // matches the viewer's own selected language (mirrors applyRemoteTranscriptUpdate); otherwise
      // our own speech shows up translated into the peer's language, mixing languages in the log.
      const matchesViewerLanguage = this.translationEnabled && updateLanguage &&
        this.normalizeLanguageTag(updateLanguage) === this.normalizeLanguageTag(this.languageSelectTarget.value);

      // The peer still needs every language, so relay unconditionally; tie the relay to our current
      // source line for this speaker so the peer can attach it even when we don't display it.
      let broadcastSourceLineId = this.latestLocalSourceLineId(localMeta);
      if (matchesViewerLanguage) {
        this.translationTranscript.language = updateLanguage;
        const sourceLine = this.applyTranslationUpdate(data.target_transcript_update, localMeta, updateLanguage);
        this.renderTranslationTranscript();
        broadcastSourceLineId = sourceLine?.sourceLineId || broadcastSourceLineId;
      }

      this.broadcastTranscriptUpdate("translation", data.target_transcript_update, updateLanguage, { sourceLineId: broadcastSourceLineId });
      this.renderDebug();
      return;
    }

    if (data.error) {
      this.receivedTerminalEvent = true;
      this.resolveFinalization();
      const requestType = data.error.request_type ? ` [${data.error.request_type}]` : "";
      this.lastSocketError = `${data.error.error_message || "DeepL a retourne une erreur."}${requestType}`;
      this.renderDebug();
      this.setError(`${data.error.error_message || "DeepL a retourne une erreur."}${requestType}`);
      this.teardown({ preserveTranscript: true });
      return;
    }

    if (data.end_of_stream) {
      this.receivedTerminalEvent = true;
      this.resolveFinalization();
      this.renderDebug();
      if (!this.intentionalStop && this.transcriptMessageCount === 0) {
        if (this.chunksWithSignalCount > 0) {
          this.setError("DeepL a termine le flux sans produire de transcription, bien qu'un signal micro ait ete detecte.");
        } else {
          this.setError("DeepL a termine le flux sans recevoir d'audio exploitable. Verifiez l'autorisation micro et le microphone utilise.");
        }
        this.teardown({ preserveTranscript: true });
      }
    }
  }

  isLikelySilenceHallucination() {
    const referenceTime = this.lastVoiceDetectedAt || this.streamingStartedAt;
    return Boolean(referenceTime) && Date.now() - referenceTime > HALLUCINATION_SILENCE_GATE_MS;
  }

  // True when the update's concluded text matches the tentative currently on screen (either
  // containing it or being contained by it, punctuation/case-insensitive): DeepL is finalizing
  // words the viewer has already seen, which real trailing speech does and hallucinations don't.
  finalizesVisibleTentative(data) {
    const update = data.source_transcript_update || data.target_transcript_update;
    const tentativeText = data.source_transcript_update
      ? this.sourceTranscript.tentativeText
      : this.translationTranscript.tentativeText;
    if (!update || !tentativeText) {
      return false;
    }

    const concludedText = this.joinSegments(update.concluded || []);
    if (!concludedText) {
      return false;
    }

    const normalizedConcluded = this.normalizeForComparison(concludedText);
    const normalizedTentative = this.normalizeForComparison(tentativeText);
    if (!normalizedConcluded || !normalizedTentative) {
      return false;
    }

    return normalizedConcluded.includes(normalizedTentative) || normalizedTentative.includes(normalizedConcluded);
  }

  // Lowercased, punctuation-stripped form for recognizing the same utterance across conclusion
  // rewording ("C'est ce que j'ai fait." vs "c'est ce que j'ai fait").
  normalizeForComparison(text) {
    return (text || "")
      .toLowerCase()
      .replace(/[.,;:!?…'’"«»()[\]-]/g, " ")
      .replace(/\s+/g, " ")
      .trim();
  }

  // A hallucinated tentative would otherwise linger on screen for as long as the silence lasts.
  discardStaleTentative() {
    if (!this.sourceTranscript.tentativeText && !this.translationTranscript.tentativeText) {
      return;
    }

    this.clearTentativeBuffers();
    this.renderSourceTranscript();
  }

  clearTentativeBuffers() {
    this.sourceTranscript.tentativeText = "";
    this.sourceTranscript.tentativeSpeakerTabId = null;
    this.sourceTranscript.tentativeSpeakerId = null;
    this.sourceTranscript.tentativeSpeakerName = "";
    this.translationTranscript.tentativeText = "";
    this.translationTranscript.tentativeSpeakerTabId = null;
    this.translationTranscript.tentativeSpeakerId = null;
    this.translationTranscript.tentativeSpeakerName = "";
  }

  applyTranscriptUpdate(buffer, update, metadata = {}) {
    const concludedText = this.joinSegments(update.concluded || []);
    let tentativeText = this.joinSegments(update.tentative || []);

    let sourceLine = null;
    if (concludedText) {
      sourceLine = this.pushRecentTranscriptLine(buffer, concludedText, metadata);
    }
    // DeepL can still carry just-concluded words as tentative in the same update; if the stream
    // then goes quiet (silence suspension), that stale tentative stays on screen and the end of
    // the phrase reads twice.
    if (sourceLine && this.transcriptAlreadyEndsWith(sourceLine.text, tentativeText)) {
      tentativeText = "";
    }
    buffer.tentativeText = tentativeText;
    buffer.tentativeSpeakerTabId = tentativeText ? (metadata.speakerTabId || null) : null;
    buffer.tentativeSpeakerId = tentativeText ? (metadata.speakerId || null) : null;
    buffer.tentativeSpeakerName = tentativeText ? (metadata.speakerName || "") : "";
    return sourceLine;
  }

  applyTranslationUpdate(update, metadata = {}, language = null) {
    const concludedText = this.joinSegments(update.concluded || []);
    let tentativeText = this.joinSegments(update.tentative || []);

    let sourceLine = null;
    if (concludedText) {
      sourceLine = this.attachTranslationToSourceLine(concludedText, metadata, language);
    }

    // Mirror of applyTranscriptUpdate: don't keep showing tentative words that the same update
    // just concluded, or the end of the phrase reads twice once the stream goes quiet.
    if (sourceLine && this.transcriptAlreadyEndsWith(sourceLine.translationText, tentativeText)) {
      tentativeText = "";
    }
    this.translationTranscript.tentativeText = tentativeText;
    this.translationTranscript.tentativeSpeakerTabId = tentativeText ? (metadata.speakerTabId || null) : null;
    this.translationTranscript.tentativeSpeakerId = tentativeText ? (metadata.speakerId || null) : null;
    this.translationTranscript.tentativeSpeakerName = tentativeText ? (metadata.speakerName || "") : "";
    this.translationTranscript.language = language || this.translationTranscript.language;
    return sourceLine;
  }

  attachTranslationToSourceLine(text, metadata = {}, language = null) {
    const sourceLine = this.findSourceLineForTranslation(metadata, text, language);

    if (!sourceLine) {
      // No line to attach to: only show the fragment as its own orphan line when the speaker's
      // latest displayed text doesn't already end with it, else the end of the phrase repeats.
      const latestSpeakerLine = this.latestLocalSourceLine(metadata);
      if (latestSpeakerLine && this.transcriptAlreadyEndsWith(this.captionTextForLine(latestSpeakerLine), text)) {
        return null;
      }
      this.pushRecentTranscriptLine(this.translationTranscript, text, { ...metadata, translationLanguage: language });
      return null;
    }

    // DeepL re-sends translation fragments just like source segments, but a re-send can land on a
    // different line than the original: the per-line merge dedup never compares them, and the
    // fragment reads twice across consecutive lines. If the previous line of the same speaker
    // already ends with this fragment, it belongs there — don't repeat it here.
    if (!sourceLine.translationText) {
      const previousLine = this.previousSameSpeakerLine(sourceLine, metadata);
      if (previousLine && this.transcriptAlreadyEndsWith(previousLine.translationText, text)) {
        return previousLine;
      }
    }

    sourceLine.translationText = sourceLine.translationText
      ? this.mergeTranscriptLines(sourceLine.translationText, text)
      : text;
    sourceLine.translationLanguage = language || sourceLine.translationLanguage || null;
    sourceLine.translationUpdatedAt = Date.now();
    this.translationAttachCursors[this.transcriptSpeakerKey(metadata)] = sourceLine.id || 0;
    return sourceLine;
  }

  // Most recent source line spoken by this participant, used to tie a relayed translation to its
  // source line even when we don't display that translation locally (peer-destined language).
  latestLocalSourceLine(metadata = {}) {
    const speakerKey = this.transcriptSpeakerKey(metadata);
    for (let index = this.sourceTranscript.recentLines.length - 1; index >= 0; index -= 1) {
      const line = this.sourceTranscript.recentLines[index];
      if (this.transcriptSpeakerKey(line) === speakerKey) {
        return line;
      }
    }
    return null;
  }

  latestLocalSourceLineId(metadata = {}) {
    return this.latestLocalSourceLine(metadata)?.sourceLineId || null;
  }

  previousSameSpeakerLine(sourceLine, metadata = {}) {
    const speakerKey = this.transcriptSpeakerKey(metadata);
    const lines = this.sourceTranscript.recentLines;
    for (let index = lines.indexOf(sourceLine) - 1; index >= 0; index -= 1) {
      if (this.transcriptSpeakerKey(lines[index]) === speakerKey) {
        return lines[index];
      }
    }
    return null;
  }

  findSourceLineForTranslation(metadata = {}, translationText = "", language = null) {
    const speakerKey = this.transcriptSpeakerKey(metadata);
    const sameSpeakerLines = this.sourceTranscript.recentLines.filter((line) => this.transcriptSpeakerKey(line) === speakerKey);
    if (!sameSpeakerLines.length) {
      return null;
    }

    if (metadata.sourceLineId) {
      const exactLine = sameSpeakerLines.find((line) => String(line.sourceLineId || "") === String(metadata.sourceLineId));
      if (exactLine) {
        return exactLine;
      }
    }

    // Translations trail their source line, but never by much. Without an id, only lines at/after
    // the previous attachment point and spoken recently are candidates, so one line that missed
    // its translation can't shift every later translation onto ever-older lines. Lines already
    // translated into another language never absorb fragments of the new one.
    const attachCursor = Math.max(this.translationAttachCursors[speakerKey] || 0, this.translationAttachMinLineId || 0);
    const candidateLines = sameSpeakerLines.filter((line) =>
      (line.id || 0) >= attachCursor &&
      Date.now() - (line.createdAt || 0) <= TRANSLATION_ATTACH_WINDOW_MS &&
      (!line.translationLanguage || !language || this.normalizeLanguageTag(line.translationLanguage) === this.normalizeLanguageTag(language))
    );
    if (!candidateLines.length) {
      return null;
    }

    const incomingLooksLikeContinuation = /^[a-zà-ÿ0-9,;:)\]%]/.test(translationText);
    const incompleteTranslationLine = candidateLines
      .slice()
      .reverse()
      .find((line) => line.translationText && !this.endsSentence(line.translationText) && incomingLooksLikeContinuation);
    if (incompleteTranslationLine) {
      return incompleteTranslationLine;
    }

    return candidateLines.find((line) => !line.translationText) || null;
  }

  pushRecentTranscriptLine(buffer, text, metadata = {}) {
    if (!text) {
      return;
    }

    const candidateLine = this.findMergeCandidateLine(buffer, metadata);
    if (candidateLine && this.shouldMergeTranscriptLines(candidateLine, text, metadata)) {
      const mergedText = this.mergeTranscriptLines(candidateLine.text, text);
      if (mergedText && mergedText !== candidateLine.text) {
        candidateLine.text = mergedText;
        candidateLine.sourceLineId = metadata.sourceLineId || candidateLine.sourceLineId || candidateLine.id;
        candidateLine.speakerTabId = metadata.speakerTabId || candidateLine.speakerTabId || null;
        candidateLine.speakerId = metadata.speakerId || candidateLine.speakerId || null;
        candidateLine.speakerName = metadata.speakerName || candidateLine.speakerName || "";
        candidateLine.messageId = metadata.messageId || candidateLine.messageId || null;
        candidateLine.createdAt = Date.now();
      }
      // The incoming segment belongs to this line, so always return it — even when the merge
      // produced no textual change (DeepL re-sent an identical or fully-contained concluded
      // segment, which the silence-suspend/resume flow makes routine). Falling through here would
      // append a duplicate line, showing the same phrase twice back-to-back.
      return candidateLine;
    }

    // A new source line is being started, so any pending phrase boundary has now been honored.
    if (buffer === this.sourceTranscript) {
      this.phraseBoundaryPending = false;
    }

    const lineId = this.nextTranscriptLineId++;
    const line = {
      id: lineId,
      sourceLineId: metadata.sourceLineId || (buffer === this.sourceTranscript ? lineId : null),
      text,
      speakerTabId: metadata.speakerTabId || null,
      speakerId: metadata.speakerId || null,
      speakerName: metadata.speakerName || "",
      messageId: metadata.messageId || null,
      translationText: metadata.translationText || "",
      translationLanguage: metadata.translationLanguage || null,
      translationUpdatedAt: null,
      createdAt: Date.now()
    };
    buffer.recentLines.push(line);

    if (buffer === this.sourceTranscript) {
      this.attachOrphanTranslationToSourceLine(line);
    }

    return line;
  }

  // The line an incoming concluded segment belongs to is the same speaker's most recent line, not
  // necessarily the buffer tail: with two speakers, re-sent/continued segments arrive after the
  // other speaker's lines were interleaved. An exact sourceLineId match is authoritative; a match
  // by speaker only counts while the line is the tail or still recent.
  findMergeCandidateLine(buffer, metadata = {}) {
    if (metadata.sourceLineId) {
      for (let index = buffer.recentLines.length - 1; index >= 0; index -= 1) {
        const line = buffer.recentLines[index];
        if (this.sameTranscriptSpeaker(line, metadata) && line.sourceLineId && String(line.sourceLineId) === String(metadata.sourceLineId)) {
          return line;
        }
      }
    }

    const lastLine = buffer.recentLines[buffer.recentLines.length - 1] || null;
    for (let index = buffer.recentLines.length - 1; index >= 0; index -= 1) {
      const line = buffer.recentLines[index];
      if (!this.sameTranscriptSpeaker(line, metadata)) {
        continue;
      }

      if (line === lastLine || Date.now() - (line.createdAt || 0) <= INTERLEAVED_MERGE_WINDOW_MS) {
        return line;
      }

      return null;
    }

    return null;
  }

  attachOrphanTranslationToSourceLine(sourceLine) {
    const sourceSpeakerKey = this.transcriptSpeakerKey(sourceLine);
    const currentTargetLanguage = this.hasLanguageSelectTarget ? this.languageSelectTarget.value : null;
    const orphanIndex = this.translationTranscript.recentLines.findIndex((line) => {
      // An orphan left over from a previous target language must not attach to a new source line.
      if (line.translationLanguage && currentTargetLanguage &&
          this.normalizeLanguageTag(line.translationLanguage) !== this.normalizeLanguageTag(currentTargetLanguage)) {
        return false;
      }
      const sameSourceLine = line.sourceLineId && sourceLine.sourceLineId && String(line.sourceLineId) === String(sourceLine.sourceLineId);
      return sameSourceLine || (!line.sourceLineId && this.transcriptSpeakerKey(line) === sourceSpeakerKey);
    });
    if (orphanIndex === -1) {
      return;
    }

    const [orphanTranslation] = this.translationTranscript.recentLines.splice(orphanIndex, 1);
    sourceLine.translationText = orphanTranslation.text;
    sourceLine.translationLanguage = orphanTranslation.translationLanguage || this.translationTranscript.language || null;
    sourceLine.translationUpdatedAt = Date.now();
  }

  joinSegments(segments) {
    const texts = segments
      .map((segment) => (segment.text || "").trim())
      .filter(Boolean);

    // DeepL occasionally repeats a segment back-to-back inside one update; joining both would
    // bake the duplicate into the line before any merge logic can see it.
    return texts
      .filter((text, index) => index === 0 || text.toLowerCase() !== texts[index - 1].toLowerCase())
      .join(" ")
      .trim();
  }

  // True when existingText already finishes with incomingText (case-insensitive, aligned on a
  // word boundary) — i.e. displaying incomingText after it would repeat the end of the phrase.
  transcriptAlreadyEndsWith(existingText, incomingText) {
    const existing = (existingText || "").trim();
    const incoming = (incomingText || "").trim();
    if (!existing || !incoming || incoming.length > existing.length) {
      return false;
    }

    if (!existing.toLowerCase().endsWith(incoming.toLowerCase())) {
      return false;
    }

    return existing.length === incoming.length ||
      WORD_BOUNDARY_CHARS.test(existing.charAt(existing.length - incoming.length - 1));
  }

  concatTranscript(existingText, incomingText) {
    if (!incomingText) {
      return existingText;
    }
    if (!existingText) {
      return incomingText;
    }

    const needsSpace = !/[\s'’-]$/.test(existingText) && !/^[,.;:!?)]/.test(incomingText);
    return needsSpace ? `${existingText} ${incomingText}` : `${existingText}${incomingText}`;
  }

  shouldMergeTranscriptLines(previousLine, incomingText, metadata = {}) {
    const existingText = previousLine?.text || "";
    if (!existingText || !incomingText) {
      return false;
    }

    if (!this.sameTranscriptSpeaker(previousLine, metadata)) {
      return false;
    }

    if (previousLine?.sourceLineId && metadata.sourceLineId && String(previousLine.sourceLineId) !== String(metadata.sourceLineId)) {
      return false;
    }

    if (previousLine?.sourceLineId && metadata.sourceLineId && String(previousLine.sourceLineId) === String(metadata.sourceLineId)) {
      return true;
    }

    if (previousLine?.messageId && metadata.messageId && previousLine.messageId !== metadata.messageId) {
      return false;
    }

    if (previousLine?.messageId && metadata.messageId && previousLine.messageId === metadata.messageId) {
      return true;
    }

    // Same-segment evidence is case-insensitive, mirroring mergeTranscriptLines: DeepL re-sends
    // concluded segments with different capitalization after a suspend/resume cycle.
    const existingLower = existingText.toLowerCase();
    const incomingLower = incomingText.toLowerCase();

    if (incomingLower === existingLower) {
      return true;
    }

    if (incomingLower.startsWith(existingLower) || existingLower.startsWith(incomingLower)) {
      return true;
    }

    if (this.computeTranscriptOverlap(existingText, incomingText) > 0) {
      return true;
    }

    // A real pause was detected (transmission suspended for silence), so the incoming concluded
    // segment is a new phrase: don't let the fuzzy continuation heuristics stitch it onto the
    // previous line. The deterministic same-segment merges above (ids/startsWith/overlap) still apply.
    if (this.phraseBoundaryPending) {
      return false;
    }

    const lineAgeMs = Date.now() - (previousLine.createdAt || 0);
    const incomingStartsLikeContinuation = /^[a-zà-ÿ0-9,;:)\]%]/i.test(incomingText) && !this.endsSentence(existingText);
    const shortRecentLine = lineAgeMs <= CAPTION_LINE_GROUP_WINDOW_MS && existingText.length < CAPTION_LINE_MAX_LENGTH;

    if (incomingStartsLikeContinuation && shortRecentLine) {
      return true;
    }

    if (!this.endsSentence(existingText) && shortRecentLine) {
      return true;
    }

    return false;
  }

  sameTranscriptSpeaker(previousLine, metadata = {}) {
    const existingSpeakerTabId = previousLine?.speakerTabId || null;
    const incomingSpeakerTabId = metadata.speakerTabId || null;
    if (existingSpeakerTabId || incomingSpeakerTabId) {
      return existingSpeakerTabId === incomingSpeakerTabId;
    }

    const existingSpeakerId = previousLine?.speakerId || null;
    const incomingSpeakerId = metadata.speakerId || null;
    if (existingSpeakerId || incomingSpeakerId) {
      return existingSpeakerId === incomingSpeakerId;
    }

    const existingSpeakerName = (previousLine?.speakerName || "").trim().toLowerCase();
    const incomingSpeakerName = (metadata.speakerName || "").trim().toLowerCase();
    if (existingSpeakerName || incomingSpeakerName) {
      return existingSpeakerName === incomingSpeakerName;
    }

    return true;
  }

  mergeTranscriptLines(existingText, incomingText) {
    if (!existingText) {
      return incomingText;
    }

    const existingLower = existingText.toLowerCase();
    const incomingLower = incomingText.toLowerCase();

    // Containment checks are case-insensitive (DeepL re-capitalizes re-sent segments) and must
    // land on a word boundary, otherwise "…conversation" would swallow an incoming "on".
    if (incomingLower.startsWith(existingLower) &&
        (incomingText.length === existingText.length || WORD_BOUNDARY_CHARS.test(incomingText.charAt(existingText.length)))) {
      return incomingText;
    }

    if (existingLower.endsWith(incomingLower) &&
        (existingText.length === incomingText.length || WORD_BOUNDARY_CHARS.test(existingText.charAt(existingText.length - incomingText.length - 1)))) {
      return existingText;
    }

    const overlapLength = this.computeTranscriptOverlap(existingText, incomingText);
    if (overlapLength > 0) {
      return `${existingText}${incomingText.slice(overlapLength)}`;
    }

    return this.concatTranscript(existingText, incomingText);
  }

  // Case-insensitive so a re-sent segment DeepL re-capitalized ("Est-ce..." after "...est-ce")
  // still deduplicates, and word-boundary-aligned so a coincidental character overlap between
  // adjacent words ("conversation" + "normale") can't swallow letters.
  computeTranscriptOverlap(existingText, incomingText) {
    const existingLower = existingText.toLowerCase();
    const incomingLower = incomingText.toLowerCase();
    const maxLength = Math.min(existingText.length, incomingText.length);

    for (let overlapLength = maxLength; overlapLength >= MIN_TRANSCRIPT_OVERLAP_CHARS; overlapLength -= 1) {
      if (existingLower.slice(-overlapLength) !== incomingLower.slice(0, overlapLength)) {
        continue;
      }

      const startsAtWordBoundary = overlapLength === existingText.length ||
        WORD_BOUNDARY_CHARS.test(existingText.charAt(existingText.length - overlapLength - 1));
      const endsAtWordBoundary = overlapLength === incomingText.length ||
        WORD_BOUNDARY_CHARS.test(incomingText.charAt(overlapLength));
      if (startsAtWordBoundary && endsAtWordBoundary) {
        return overlapLength;
      }
    }

    return 0;
  }

  endsSentence(text) {
    return /[.!?…][)"'\]]?\s*$/.test((text || "").trim());
  }

  renderSourceTranscript() {
    this.renderTranscripts();
  }

  renderTranslationTranscript() {
    this.syncTranslationControls();
    this.renderTranscripts();
  }

  renderTranscripts() {
    const entries = this.collectCaptionEntries();
    const html = this.renderCaptionBlocksHtml(entries);

    this.sourceOutputTarget.innerHTML = html;
    this.sourceEmptyTarget.hidden = html.length > 0;

    if (html.length > 0) {
      this.sourceOutputTarget.scrollTop = this.sourceOutputTarget.scrollHeight;
    }
  }

  // Build the ordered list of caption segments shown in the log. sourceTranscript.recentLines holds
  // both participants' speech in the order it was spoken (our own lines tagged local, the peer's
  // relayed over the meeting channel). Each speaker's words are shown translated into the local
  // viewer's language when a translation is available, otherwise in their original language.
  collectCaptionEntries() {
    const entries = [];

    this.sourceTranscript.recentLines.forEach((srcLine) => {
      const text = this.captionTextForLine(srcLine);
      if (!text) {
        return;
      }

      entries.push({
        text,
        speakerTabId: srcLine.speakerTabId,
        speakerId: srcLine.speakerId,
        speakerName: srcLine.speakerName
      });
    });

    // Translations that arrived before their matching source line (no sourceLineId yet); shown so
    // their content isn't lost in the brief window before the source line attaches them.
    if (this.translationEnabled) {
      this.translationTranscript.recentLines
        .filter((line) => !line.sourceLineId)
        .forEach((line) => {
          entries.push({
            text: line.text,
            speakerTabId: line.speakerTabId,
            speakerId: line.speakerId,
            speakerName: line.speakerName
          });
        });
    }

    const tentative = this.tentativeCaptionEntry();
    if (tentative) {
      // Render-time safety net: a tentative that lags behind an already-concluded line (frequent
      // right before silence suspends the stream) would repeat the end of the phrase.
      const lastEntry = entries[entries.length - 1];
      const duplicatesTail = lastEntry &&
        this.sameTranscriptSpeaker(lastEntry, tentative) &&
        this.transcriptAlreadyEndsWith(lastEntry.text, tentative.text);
      if (!duplicatesTail) {
        entries.push(tentative);
      }
    }

    return entries;
  }

  captionTextForLine(srcLine) {
    if (this.translationEnabled && srcLine.translationText) {
      return srcLine.translationText;
    }

    return srcLine.text;
  }

  // In-progress speech for whoever is currently talking (local or remote): prefer the translated
  // tentative (viewer's language), fall back to the raw source tentative.
  tentativeCaptionEntry() {
    if (this.translationEnabled && this.translationTranscript.tentativeText) {
      return {
        text: this.translationTranscript.tentativeText,
        speakerTabId: this.translationTranscript.tentativeSpeakerTabId,
        speakerId: this.translationTranscript.tentativeSpeakerId,
        speakerName: this.translationTranscript.tentativeSpeakerName,
        tentative: true
      };
    }

    if (this.sourceTranscript.tentativeText) {
      return {
        text: this.sourceTranscript.tentativeText,
        speakerTabId: this.sourceTranscript.tentativeSpeakerTabId,
        speakerId: this.sourceTranscript.tentativeSpeakerId,
        speakerName: this.sourceTranscript.tentativeSpeakerName,
        tentative: true
      };
    }

    return null;
  }

  // Append each segment to the open block when it shares the previous segment's speaker; otherwise
  // close that block and start a new one headed by the speaker's avatar + name.
  renderCaptionBlocksHtml(entries) {
    const blocks = [];

    entries.forEach((entry) => {
      if (!entry?.text) {
        return;
      }

      const currentBlock = blocks[blocks.length - 1];
      if (currentBlock && this.sameTranscriptSpeaker(currentBlock, entry)) {
        currentBlock.segments.push(entry);
      } else {
        blocks.push({
          speakerTabId: entry.speakerTabId,
          speakerId: entry.speakerId,
          speakerName: entry.speakerName,
          segments: [entry]
        });
      }
    });

    return blocks.map((block) => this.renderSpeakerBlockHtml(block)).join("");
  }

  renderSpeakerBlockHtml(block) {
    const rawName = block.speakerName || "Interlocuteur";
    const speakerName = this.escapeHtml(rawName);
    const avatarHtml = this.renderSpeakerAvatarHtml(rawName, block);

    const bodyHtml = block.segments
      .map((segment) => {
        const classes = ["live-captions__block-text"];
        if (segment.tentative) {
          classes.push("live-captions__block-text--tentative");
        }
        return `<span class="${classes.join(" ")}">${this.escapeHtml(segment.text)}</span>`;
      })
      .join(" ");

    return `<div class="live-captions__block">` +
      `<div class="live-captions__block-header">${avatarHtml}<span class="live-captions__block-speaker">${speakerName}</span></div>` +
      `<div class="live-captions__block-body">${bodyHtml}</div>` +
      `</div>`;
  }

  renderSpeakerAvatarHtml(name, block) {
    const initial = this.escapeHtml((name.trim().charAt(0) || "?").toUpperCase());
    const color = this.avatarColorForSpeaker(block);

    return `<span class="live-captions__avatar" style="background:${color}">${initial}</span>`;
  }

  avatarColorForSpeaker(block) {
    const key = this.transcriptSpeakerKey(block);
    let hash = 0;
    for (let index = 0; index < key.length; index += 1) {
      hash = (hash * 31 + key.charCodeAt(index)) >>> 0;
    }

    return SPEAKER_AVATAR_COLORS[hash % SPEAKER_AVATAR_COLORS.length];
  }

  transcriptSpeakerKey(line) {
    if (line?.speakerTabId) {
      return `tab:${line.speakerTabId}`;
    }

    return `user:${line?.speakerId || ""}`;
  }

  escapeHtml(text) {
    const div = document.createElement("div");
    div.textContent = text;
    return div.innerHTML;
  }


  async decodeSocketPayload(data) {
    if (typeof data === "string") {
      return data;
    }

    if (data instanceof Blob) {
      return data.text();
    }

    if (data instanceof ArrayBuffer) {
      return new TextDecoder().decode(data);
    }

    if (ArrayBuffer.isView(data)) {
      return new TextDecoder().decode(data);
    }

    throw new Error(`Type de message websocket inattendu: ${Object.prototype.toString.call(data)}`);
  }

  async postJson(url, payload) {
    const csrfToken = document.querySelector("meta[name='csrf-token']")?.getAttribute("content");
    const headers = {
      "Content-Type": "application/json",
      "Accept": "application/json"
    };

    if (csrfToken) {
      headers["X-CSRF-Token"] = csrfToken;
    }

    const response = await fetch(url, {
      method: "POST",
      headers,
      body: JSON.stringify(payload)
    });

    const body = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(body.error || `Erreur HTTP ${response.status}`);
    }

    return body;
  }

  subscribeToMeetingChannel() {
    if (!this.hasMeetingIdValue || !this.meetingIdValue) {
      return;
    }

    this.meetingChannelSubscription = consumer.subscriptions.create(
      { channel: "LiveCaptionsChannel", meeting_id: this.meetingIdValue, meeting_token: this.meetingTokenValue },
      {
        connected: () => {
          this.meetingChannelConnected = true;
          this.broadcastTranslationPreferences({ responseRequested: true });
          this.renderDebug();
        },
        disconnected: () => {
          this.meetingChannelConnected = false;
          this.renderDebug();
        },
        received: (data) => {
          this.handleMeetingChannelMessage(data);
        }
      }
    );
  }

  unsubscribeFromMeetingChannel() {
    if (!this.meetingChannelSubscription) {
      return;
    }

    consumer.subscriptions.remove(this.meetingChannelSubscription);
    this.meetingChannelSubscription = null;
    this.meetingChannelConnected = false;
  }

  handleMeetingChannelMessage(data) {
    if (!data) {
      return;
    }

    if (data.sender_tab_id && data.sender_tab_id === this.tabId) {
      return;
    }

    if (!data.sender_tab_id && Number(data.sender_user_id) === this.currentUserIdValue) {
      return;
    }

    if (data.type === "preferences") {
      this.applyPeerPreferences(data);
      return;
    }

    if (data.type === "transcript_update") {
      this.applyRemoteTranscriptUpdate(data);
    }
  }

  applyPeerPreferences(data) {
    const nextTranslationEnabled = Boolean(data.translation_enabled);
    const nextTargetLanguage = nextTranslationEnabled ? (data.target_language || null) : null;
    const preferencesChanged = this.peerTranslationEnabled !== nextTranslationEnabled || this.peerTargetLanguage !== nextTargetLanguage;

    this.peerTranslationEnabled = nextTranslationEnabled;
    this.peerTargetLanguage = nextTargetLanguage;
    this.renderDebug();

    if (data.response_requested) {
      this.broadcastTranslationPreferences({ responseRequested: false });
    }

    if (preferencesChanged) {
      this.autoPausedForSilence = false;
      this.queueAutomaticStreamingEvaluation();
      this.restartStreamingForPeerPreferences();
    }
  }

  applyRemoteTranscriptUpdate(data) {
    const metadata = {
      speakerTabId: data.sender_tab_id || null,
      speakerId: data.sender_user_id || null,
      speakerName: data.speaker_name || "Interlocuteur",
      sourceLineId: data.source_line_id || null
    };

    if (data.transcript_kind === "source") {
      this.applyTranscriptUpdate(this.sourceTranscript, data.transcript_update || {}, metadata);
      this.renderSourceTranscript();
      this.renderDebug();
      return;
    }

    if (data.transcript_kind === "translation" && this.translationEnabled) {
      const incomingLanguage = data.language || null;
      if (incomingLanguage && this.normalizeLanguageTag(incomingLanguage) !== this.normalizeLanguageTag(this.languageSelectTarget.value)) {
        return;
      }

      this.translationTranscript.language = incomingLanguage || this.languageSelectTarget.value;
      this.applyTranslationUpdate(data.transcript_update || {}, metadata, this.translationTranscript.language);
      this.renderTranslationTranscript();
      this.renderDebug();
    }
  }

  broadcastTranscriptUpdate(transcriptKind, transcriptUpdate, language = null, options = {}) {
    if (!this.meetingChannelSubscription || !this.meetingChannelConnected) {
      return;
    }

    this.meetingChannelSubscription.perform("relay_transcript", {
      sender_tab_id: this.tabId,
      sender_user_id: this.currentUserIdValue,
      source_line_id: options.sourceLineId || null,
      transcript_kind: transcriptKind,
      transcript_update: transcriptUpdate,
      language,
      speaker_name: this.speakerNameValue
    });
  }

  broadcastTranslationPreferences({ responseRequested = false } = {}) {
    if (!this.meetingChannelSubscription || !this.meetingChannelConnected) {
      return;
    }

    this.meetingChannelSubscription.perform("update_preferences", {
      sender_tab_id: this.tabId,
      sender_user_id: this.currentUserIdValue,
      response_requested: responseRequested,
      translation_enabled: this.translationEnabled,
      target_language: this.translationEnabled ? this.languageSelectTarget.value : null
    });
  }

  targetLanguagesChanged() {
    return this.targetLanguagesSignature(this.activeTargetLanguages || []) !== this.targetLanguagesSignature(this.outgoingTargetLanguages);
  }

  targetLanguagesSignature(languages) {
    return [...languages].map((language) => this.normalizeLanguageTag(language)).sort().join(",");
  }

  async restartStreamingForLocalPreferences(statusMessage = "Mise a jour de la langue de traduction...") {
    if (!this.isRunning || this.isRestartingForLocalPreferences || this.isRestartingForPeerPreferences) {
      return;
    }

    this.isRestartingForLocalPreferences = true;
    this.setStatus(statusMessage, PENDING_STATUS);
    const restartAutomaticStreaming = this.automaticStreaming;

    try {
      await this.stopStreaming({ preserveTranscript: true, finalStatusMessage: null });
      if (!this.element.isConnected) {
        return;
      }
      await this.startStreaming({ preserveTranscript: true, automatic: restartAutomaticStreaming });
    } finally {
      this.isRestartingForLocalPreferences = false;
    }
  }

  async restartStreamingForPeerPreferences() {
    if (!this.isRunning || this.isRestartingForPeerPreferences || this.isRestartingForLocalPreferences) {
      return;
    }

    this.isRestartingForPeerPreferences = true;
    this.setStatus("Mise a jour de la traduction demandee par l'interlocuteur...", PENDING_STATUS);
    const restartAutomaticStreaming = this.automaticStreaming;

    try {
      await this.stopStreaming({ preserveTranscript: true, finalStatusMessage: null });
      if (!this.element.isConnected) {
        return;
      }
      await this.startStreaming({ preserveTranscript: true, automatic: restartAutomaticStreaming });
    } finally {
      this.isRestartingForPeerPreferences = false;
    }
  }

  resetRemoteTranslationBuffer() {
    // The translation context changed (language switch or toggle): only lines created from now on
    // may receive translations, so in-flight fragments of the previous language can't attach to
    // lines they no longer belong to.
    this.translationAttachMinLineId = this.nextTranscriptLineId || 0;
    this.translationTranscript = {
      recentLines: [],
      tentativeText: "",
      tentativeSpeakerTabId: null,
      tentativeSpeakerId: null,
      tentativeSpeakerName: "",
      language: null
    };
  }

  resolveFinalization() {
    if (this.finalizationResolver) {
      this.finalizationResolver();
      this.finalizationResolver = null;
    }
  }

  startFirstChunkWatchdog() {
    this.clearFirstChunkWatchdog();
    this.firstChunkTimeout = window.setTimeout(() => {
      if (this.chunksProducedCount === 0 && this.isRunning) {
        this.setError("Le micro est autorise mais aucun chunk audio n'est produit. Essayez Chrome ou Edge et verifiez le micro selectionne.");
      }
    }, FIRST_CHUNK_TIMEOUT_MS);
  }

  clearFirstChunkWatchdog() {
    if (this.firstChunkTimeout) {
      window.clearTimeout(this.firstChunkTimeout);
      this.firstChunkTimeout = null;
    }
  }

  renderDebug() {
    if (!this.hasDebugOutputTarget) {
      return;
    }

    this.debugOutputTarget.textContent = [
      `meeting_id: ${this.meetingIdValue || "n/a"}`,
      `current_user_id: ${this.currentUserIdValue || "n/a"}`,
      `speaker_name: ${this.speakerNameValue || "n/a"}`,
      `meeting_channel_connected: ${this.meetingChannelConnected}`,
      `peer_translation_enabled: ${this.peerTranslationEnabled}`,
      `peer_target_language: ${this.peerTargetLanguage || "none"}`,
      `weglot_language: ${this.lastWeglotLanguage || this.currentWeglotLanguage() || "none"}`,
      `manual_target_language_selected: ${this.manualTargetLanguageSelected}`,
      `manual_streaming_override: ${this.userToggledStreaming}`,
      `automatic_streaming: ${this.automaticStreaming}`,
      `automatic_translation_needed: ${this.automaticTranslationNeeded()}`,
      `automatic_block_reason: ${this.lastAutomaticBlockReason || "none"}`,
      `automatic_error: ${this.lastAutomaticStreamingError || "none"}`,
      `auto_paused_for_silence: ${this.autoPausedForSilence}`,
      `jitsi_joined: ${this.jitsiState?.conferenceJoined}`,
      `jitsi_participant_count: ${this.jitsiState?.participantCount || 0}`,
      `jitsi_local_audio_muted: ${this.jitsiState?.localAudioMuted}`,
      `jitsi_local_audio_available: ${this.jitsiState?.localAudioAvailable}`,
      `jitsi_remote_audio_muted: ${JSON.stringify(this.jitsiState?.remoteAudioMuted || {})}`,
      `jitsi_current_audio_input_device_id: ${this.jitsiState?.currentAudioInputDevice?.deviceId || "default"}`,
      `jitsi_current_audio_input_device_label: ${this.jitsiState?.currentAudioInputDevice?.label || "n/a"}`,
      `jitsi_state_reason: ${this.jitsiState?.reason || "none"}`,
      `local_translation_enabled: ${this.translationEnabled}`,
      `local_target_language: ${this.languageSelectTarget.value || "none"}`,
      `outgoing_target_languages: ${this.outgoingTargetLanguages.join(",") || "none"}`,
      `active_target_languages: ${(this.activeTargetLanguages || []).join(",") || "none"}`,
      `source_language: ${this.currentSourceLanguage || "auto"}`,
      `source_language_mode: ${this.currentSourceLanguageMode}`,
      "transport: microphone",
      `source_media_content_type: ${this.sourceMediaContentType || "n/a"}`,
      `selected_microphone_id: ${this.selectedMicrophoneId || "default"}`,
      `selected_microphone_label: ${this.selectedMicrophoneLabel()}`,
      `recorder_mime_type: ${this.recorderMimeType || "n/a"}`,
      `audio_sample_rate: ${this.audioSampleRate || "n/a"}`,
      `target_audio_sample_rate: ${this.targetAudioSampleRate || "n/a"}`,
      `capture_track_label: ${this.captureTrack?.label || "n/a"}`,
      `capture_track_enabled: ${this.captureTrack ? this.captureTrack.enabled : "n/a"}`,
      `capture_track_muted: ${this.captureTrack ? this.captureTrack.muted : "n/a"}`,
      `capture_track_ready_state: ${this.captureTrack?.readyState || "n/a"}`,
      `capture_track_settings: ${this.formatCaptureTrackSettings(this.captureTrackSettings)}`,
      `session_id: ${this.sessionId || "n/a"}`,
      `chunks_produced: ${this.chunksProducedCount}`,
      `chunks_sent: ${this.chunksSentCount}`,
      `chunks_with_signal: ${this.chunksWithSignalCount}`,
      `voice_activity_threshold: ${VOICE_ACTIVITY_THRESHOLD.toFixed(4)}`,
      `speech_start_chunks_required: ${AUTO_SPEECH_START_CHUNKS}`,
      `suspended_voice_streak: ${this.suspendedVoiceStreak || 0}`,
      `silence_chunk_streak: ${this.silenceChunkStreak}`,
      `last_chunk_bytes: ${this.lastChunkByteSize}`,
      `last_raw_chunk_rms: ${this.lastRawChunkRms.toFixed(4)}`,
      `max_raw_chunk_rms: ${this.maxRawChunkRms.toFixed(4)}`,
      `last_chunk_rms: ${this.lastChunkRms.toFixed(4)}`,
      `max_chunk_rms: ${this.maxChunkRms.toFixed(4)}`,
      `transcript_messages: ${this.transcriptMessageCount}`,
      `hallucination_drops: ${this.hallucinationDropCount || 0}`,
      `last_socket_event: ${this.lastSocketEvent}`,
      `last_socket_error: ${this.lastSocketError}`
    ].join("\n");
  }

  floatTo16BitSample(sample) {
    const normalized = Math.max(-1, Math.min(1, sample));
    return normalized < 0 ? Math.round(normalized * 0x8000) : Math.round(normalized * 0x7fff);
  }

  preferredDeepLSampleRate(sampleRate) {
    const roundedSampleRate = Math.round(sampleRate || 0);
    // DeepL recommends 16 kHz PCM. When the AudioContext already runs at 16 kHz we use it
    // directly; otherwise we still target 16 kHz and let resampleChannelData() downsample.
    if (roundedSampleRate === PREFERRED_DEEPL_SAMPLE_RATE && PCM_SUPPORTED_SAMPLE_RATES.includes(roundedSampleRate)) {
      return roundedSampleRate;
    }

    return PREFERRED_DEEPL_SAMPLE_RATE;
  }

  resampleChannelData(channelData, inputRate, outputRate) {
    if (!channelData.length || inputRate === outputRate) {
      return channelData;
    }

    const sampleRatio = inputRate / outputRate;
    const outputLength = Math.max(1, Math.round(channelData.length / sampleRatio));
    const output = new Float32Array(outputLength);

    for (let index = 0; index < outputLength; index += 1) {
      const start = Math.floor(index * sampleRatio);
      const end = Math.min(channelData.length, Math.floor((index + 1) * sampleRatio));
      let sum = 0;
      let count = 0;

      for (let cursor = start; cursor < Math.max(start + 1, end); cursor += 1) {
        sum += channelData[cursor];
        count += 1;
      }

      output[index] = count > 0 ? (sum / count) : channelData[Math.min(start, channelData.length - 1)];
    }

    return output;
  }

  calculateInt16Rms(samples) {
    if (!samples.length) {
      return 0;
    }

    let total = 0;
    for (let index = 0; index < samples.length; index += 1) {
      const normalized = samples[index] / 32768;
      total += normalized * normalized;
    }

    return Math.sqrt(total / samples.length);
  }

  formatCaptureTrackSettings(settings) {
    if (!settings || Object.keys(settings).length === 0) {
      return "n/a";
    }

    const compactSettings = {
      channelCount: settings.channelCount,
      sampleRate: settings.sampleRate,
      sampleSize: settings.sampleSize,
      latency: settings.latency,
      autoGainControl: settings.autoGainControl,
      echoCancellation: settings.echoCancellation,
      noiseSuppression: settings.noiseSuppression,
      voiceIsolation: settings.voiceIsolation
    };

    return JSON.stringify(compactSettings);
  }

  selectedMicrophoneLabel() {
    if (!this.hasMicrophoneSelectTarget) {
      return "n/a";
    }

    return this.microphoneSelectTarget.selectedOptions[0]?.textContent || "Microphone par défaut";
  }

  buildTabId() {
    const randomValue = window.crypto?.getRandomValues
      ? window.crypto.getRandomValues(new Uint32Array(2)).join("-")
      : `${Date.now()}-${Math.random().toString(16).slice(2)}`;

    return `live-captions-tab-${randomValue}`;
  }

  calculateFloatRms(samples) {
    if (!samples.length) {
      return 0;
    }

    let total = 0;
    for (let index = 0; index < samples.length; index += 1) {
      total += samples[index] * samples[index];
    }

    return Math.sqrt(total / samples.length);
  }

  int16SamplesToBase64(samples) {
    const buffer = new ArrayBuffer(samples.length * 2);
    const view = new DataView(buffer);

    samples.forEach((sample, index) => {
      view.setInt16(index * 2, sample, true);
    });

    return this.arrayBufferToBase64(buffer);
  }

  arrayBufferToBase64(buffer) {
    const bytes = buffer instanceof Uint8Array ? buffer : new Uint8Array(buffer);
    const chunkSize = 0x8000;
    let binary = "";

    for (let index = 0; index < bytes.length; index += chunkSize) {
      binary += String.fromCharCode(...bytes.subarray(index, index + chunkSize));
    }

    return window.btoa(binary);
  }
}
